import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';

/**
 * i18n-lite do módulo Armazém (PRD-WMS §2.3 / §8): rótulos de UI em PT-BR
 * servidos por GET /warehouse/labels (tabela `wms_label_texts`), com FALLBACK
 * local — o dicionário abaixo garante que a UI não quebra se o backend do M0
 * ainda não subiu ou se uma chave não foi semeada.
 *
 * Terminologia obrigatória (correções do dono, ARQUITETURA-E-ADAPTACOES.md):
 * "Supervisor" (NUNCA "Môr"), "OS (Ordem de Serviço)", "LPN".
 */
export const WAREHOUSE_LABEL_FALLBACK = {
    // Página de configurações
    'wms.config.title': 'Configurações do Armazém',
    'wms.config.subtitle':
        'Cadastros do módulo de armazém: operadores, equipamentos, prefixos de LPN, tipos de OS (Ordem de Serviço), depósitos e parâmetros.',
    'wms.tab.operators': 'Operadores',
    'wms.tab.equipment': 'Equipamentos',
    'wms.tab.lpn_prefixes': 'Prefixos de LPN',
    'wms.tab.service_order_types': 'Tipos de OS',
    'wms.tab.deposits': 'Depósitos',
    'wms.tab.settings': 'Parâmetros',

    // Seções (título + descrição por aba)
    'wms.operators.title': 'Operadores',
    'wms.operators.description':
        'Vínculo entre um funcionário (cadastro em Funcionários, no menu do sistema) e a Função dele no WMS — Supervisor ou Operador. A Função não é o cargo; o crachá (ID do funcionário) vive no cadastro da pessoa.',
    'wms.equipment.title': 'Equipamentos',
    'wms.equipment.description': 'Empilhadeiras e transpaleteiras. O código identifica o equipamento nas OSs e no SLA.',
    'wms.lpn_prefixes.title': 'Prefixos de LPN',
    'wms.lpn_prefixes.description':
        'Prefixos usados na geração de códigos de LPN (ex.: MP, PA, REV). O prefixo é identidade, nunca comportamento.',
    'wms.service_order_types.title': 'Tipos de OS (Ordem de Serviço)',
    'wms.service_order_types.description':
        'Tipos extensíveis por cadastro. O comportamento do sistema chaveia no behavior_key — o código é só identidade.',
    'wms.deposits.title': 'Depósitos',
    'wms.deposits.description':
        'O comportamento do depósito vem das flags — nunca do nome: vendável (conta como vendável), consumível (consumível pela fábrica) e espelho Bling (entra no espelho Bling).',
    'wms.settings.title': 'Parâmetros',
    'wms.settings.description':
        'Parâmetros do módulo (chave → valor JSON), editáveis sem código. Os parâmetros de rota do seed aparecem aqui.',

    // Campos de formulário
    'wms.field.name': 'Nome',
    'wms.field.code': 'Código',
    'wms.field.employee': 'Funcionário',
    'wms.field.employee_code': 'ID do funcionário',
    'wms.field.label': 'Rótulo',
    'wms.field.role': 'Função',
    'wms.field.bling_deposit_id': 'ID do depósito no Bling',
    'wms.field.type': 'Tipo',
    'wms.field.behavior_key': 'Comportamento (behavior_key)',
    'wms.field.key': 'Chave',
    'wms.field.value': 'Valor (JSON)',

    // Enums
    'wms.role.SUPERVISOR': 'Supervisor',
    // 25/08 — papéis do crachá (picking/packing/fabrica/supervisor).
    'wms.operators.roles': 'Funções',
    'wms.operator_role.picking': 'Picking',
    'wms.operator_role.packing': 'Packing',
    'wms.operator_role.fabrica': 'Fábrica',
    'wms.operator_role.supervisor': 'Supervisor (tudo)',
    'wms.collector.no_roles': 'Este crachá não tem nenhum papel atribuído. Só a consulta livre está disponível — fale com o supervisor.',
    'wms.role.OPERATOR': 'Operador',
    'wms.equipment_type.FORKLIFT': 'Empilhadeira',
    'wms.equipment_type.PALLET_JACK': 'Transpaleteira',
    'wms.behavior.LIFT': 'Movimentação vertical',
    'wms.behavior.TRANSPORT': 'Transporte',
    'wms.behavior.PRODUCTION': 'Produção',
    'wms.behavior.REPLENISHMENT': 'Reabastecimento de máquina',
    'wms.behavior.REPACK': 'Reorganização de LPN',
    'wms.behavior.SHIPPING': 'Expedição',
    'wms.behavior.COUNT': 'Inventário/Auditoria',
    'wms.behavior.GENERIC': 'Genérico',

    // Flags de depósito (explicação curta do contrato)
    'wms.deposit.is_sellable': 'Vendável',
    'wms.deposit.is_sellable_help': 'Conta como vendável.',
    'wms.deposit.is_consumable': 'Consumível',
    'wms.deposit.is_consumable_help': 'Consumível pela fábrica.',
    'wms.deposit.mirror_to_bling': 'Replicar para o Bling',
    'wms.deposit.mirror_to_bling_help': 'Replica o saldo deste depósito para o Bling (exige o ID do depósito no Bling).',
    'wms.deposit.bling_deposit_id_help': 'ID do depósito correspondente no Bling — para onde o saldo será replicado.',

    // Localizações (M1)
    'wms.tab.locations': 'Localizações',
    'wms.locations.title': 'Localizações',
    'wms.locations.description':
        'Endereços físicos do armazém: racks (rack, bloco, andar, posição), stagings, docas, fábrica e incidentes. Ajuste status, pesos e larguras, imprima as etiquetas de endereço — e crie racks no Editar galpão.',
    'wms.locations.bulk_edit': 'Edição em massa',
    'wms.locations.new_location': 'Nova localização',
    'wms.locations.print_labels': 'Etiquetas',
    'wms.locations.partial_list': 'lista truncada — refine os filtros para ver o restante',
    'wms.locations.all_racks': 'Todos os racks',
    'wms.locations.no_racks': 'Nenhum rack cadastrado',

    // Exclusão de rack/posição (setup do M1 — recriar na orientação certa).
    // Só apaga se VAZIO; ocupado => a tela mostra a mensagem do 409 (o que ocupa).
    'wms.locations.delete_rack': 'Excluir rack',
    'wms.locations.delete_position': 'Excluir posição',
    'wms.locations.delete_rack_title': 'Excluir rack',
    'wms.locations.delete_position_title': 'Excluir posição',
    'wms.locations.delete_rack_confirm':
        'Isto exclui o RACK inteiro — todas as posições (blocos × andares e o staging da cabeceira). Só funciona se o rack estiver vazio: nenhuma posição pode ter pallet (LPN) nem reserva. A exclusão não é desfeita pela tela.',
    'wms.locations.delete_position_confirm':
        'Isto exclui esta posição. Só funciona se ela estiver vazia: sem pallet (LPN) e sem reserva. A exclusão não é desfeita pela tela.',
    'wms.locations.delete_confirm_button': 'Excluir',
    'wms.locations.delete_occupied_hint': 'Ocupada — esvazie antes de excluir:',
    'wms.locations.deleted_rack_toast': 'Rack excluído',
    'wms.locations.deleted_position_toast': 'Posição excluída',
    'wms.locations.delete_error': 'Não foi possível excluir.',

    // Localizações — campos (não existe mais rua: rack é letra, bloco é sequencial)
    'wms.field.block': 'Bloco',
    'wms.field.rack': 'Rack',
    'wms.field.bay': 'Bloco',
    'wms.field.level': 'Andar',
    'wms.field.position': 'Posição',
    'wms.field.bay_width': 'Largura do vão (m)',
    'wms.field.max_weight_kg': 'Peso máx. (kg)',
    'wms.field.status': 'Status',
    'wms.field.sector': 'Setor',
    'wms.field.notes': 'Observações',

    // Localizações — enums
    'wms.location_type.RACK': 'Rack',
    'wms.location_type.STAGING_STREET': 'Staging do rack (cabeceira)',
    // "Staging" e não "Staging de doca": o tipo virou curinga e pode nascer
    // longe da doca (o id do enum segue STAGING_DOCK — trocar enum é migration).
    'wms.location_type.STAGING_DOCK': 'Staging',
    'wms.location_type.DOCK': 'Doca',
    'wms.location_type.FACTORY_IN': 'Fábrica — entrada',
    'wms.location_type.FACTORY_OUT': 'Fábrica — saída',
    'wms.location_type.INCIDENT': 'Incidente',
    'wms.location_status.ACTIVE': 'Ativa',
    'wms.location_status.BLOCKED': 'Bloqueada',

    // Gerador de rack (porta-pallet) — o formulário vive no Editar galpão
    'wms.generator.title': 'Criar rack',
    'wms.generator.description':
        'Gera todas as posições do rack (blocos × andares × P1/P2) e o staging da cabeceira. Blocos são sequenciais (1, 2, 3…); de frente para o rack, P1 é a direita e P2 a esquerda; A1 é o térreo e não tem limite de peso.',
    'wms.generator.rack_label': 'Prefixo do rack',
    'wms.generator.levels': 'Andares',
    'wms.generator.blocks': 'Blocos',
    // "Blocos largos por ponta" MORREU em 11/08: a geração aplica UMA largura a
    // todos os blocos, e vão diferente vira edição depois da rua gerada
    // (decisão do dono). A regra automática de 2,50 nas pontas era a planta
    // DESTE galpão escrita em código — o oposto do que o §5 do PRD manda.
    'wms.generator.weights_title': 'Peso máximo por andar (kg)',
    'wms.generator.overrides_title': 'Exceções por bloco',
    'wms.generator.overrides_help':
        'Ajuste pontual de um bloco específico: largura do vão e/ou nº de andares diferentes do padrão do rack.',
    'wms.generator.add_override': 'Adicionar exceção',
    'wms.generator.remove_override': 'Remover exceção',
    'wms.generator.override_needs_value': 'Informe largura e/ou andares para a exceção.',
    'wms.generator.submit': 'Gerar',
    'wms.generator.rack_exists': 'Este rack já existe.',
    'wms.generator.success_title': 'Rack gerado',
    'wms.generator.codes_sample': 'Amostra dos códigos gerados',

    // Edição em massa (texto v2 na seção "Localizações r3" adiante)
    'wms.bulk.title': 'Edição em massa',
    'wms.bulk.property': 'Campo a alterar',
    'wms.bulk.apply': 'Aplicar',

    // Etiquetas de endereço: fluxo ZPL — chaves vivem na seção "Impressoras &
    // etiquetas ZPL (M1 r2)" adiante.

    // Criação individual de localização
    'wms.location_create.title': 'Nova localização',
    'wms.location_create.description':
        'Criação individual com código literal — para docas, stagings de doca, posições de fábrica e incidentes. Racks nascem pelo gerador de rua.',
    'wms.location_create.code_help':
        'O código precisa casar exatamente o padrão do tipo, padding incluso — ex.: DCA-801, STG-DCA-80-01, FAB-RBB-IN-P1, INC-800-00-01.',

    // Mapa do galpão (4ª rodada — editor INLINE no WarehouseMap: paleta
    // arrastável, alças, duplo clique; Salvar re-pede a senha; criar rua gera
    // os DADOS REAIS via generate-rack; fixas posicionáveis; excluir rack/bloco
    // pede senha e só vale se o backend confirmar vazio)
    'wms.map.title': 'Mapa do galpão',
    'wms.map.description':
        'Mapa do galpão desenhado no Editor de galpão. Os blocos são coloridos pelo status dos endereços reais (vínculo pelo prefixo do rack); clique num bloco ou num rack para filtrar a tabela.',
    'wms.map.orientation': 'Visão a partir da doca',
    'wms.map.dock': 'Doca',
    'wms.map.legend_all_active': 'Todas ativas',
    'wms.map.legend_has_blocked': 'Contém bloqueadas',
    'wms.map.legend_no_data': 'Sem cadastro',
    'wms.map.empty': 'Nenhum rack criado ainda — use "Editar galpão".',
    'wms.map.rack': 'Rack',
    'wms.map.configure': 'Editor de galpão',
    'wms.map.not_configured': 'O mapa do galpão ainda não foi desenhado.',
    'wms.map.not_configured_hint':
        'Desenhe o galpão uma vez no Editor de galpão (áreas, racks e blocos); depois disso o mapa colore os blocos pelo status dos endereços reais. O uso do mapa é opcional — a tabela e os filtros funcionam sem ele.',
    'wms.map.saved': 'Layout do galpão salvo.',
    'wms.map.unsaved': 'Alterações não salvas',
    'wms.map.discard_confirm': 'Descartar',
    'wms.map.discarded': 'Alterações descartadas.',
    'wms.map.layout_not_saved':
        'O rack foi criado nos dados reais, mas o mapa não foi salvo — salve de novo.',
    'wms.map.save_again': 'Salvar de novo',
    'wms.map.palette_title': 'Elementos',
    'wms.map.palette_hint': 'Área: arraste o chip e solte no mapa. Rack: clique no chip e trace com dois cliques.',
    'wms.map.palette.logistica': 'Área Logística',
    'wms.map.palette.doca': 'Área Doca',
    'wms.map.palette.staging': 'Área Staging',
    'wms.map.palette.producao': 'Área Produção',
    'wms.map.palette.buffer': 'Área Buffer',
    'wms.map.palette.outro': 'Área Outro',
    'wms.map.palette.rack': 'Rack',
    'wms.map.unplaced_racks': 'Racks sem posição no mapa',
    'wms.map.unplaced_racks_hint': 'Clique no rack e trace o desenho dele no mapa — só o desenho; os endereços já existem.',
    'wms.map.gesture_hint':
        'Área: arraste da paleta · Rack: chip + dois cliques · clique seleciona · arraste move · alças redimensionam · duplo clique edita · Delete apaga áreas',
    'wms.map.area_created': 'Área criada — arraste para posicionar e ajuste o tamanho pelas alças.',
    'wms.map.rack_created': 'Rack criado — endereços reais gerados e mapa salvo.',
    'wms.map.rack_placed': 'Rack posicionado no mapa — desenho salvo.',
    'wms.map.trace_click_start': 'Clique no início do rack (onde fica o Bloco 1).',
    'wms.map.trace_click_end': 'Clique no fim do rack — a orientação e o comprimento saem do traçado.',
    'wms.map.trace_redo_hint': 'Confira os parâmetros e crie o rack; clique no mapa para refazer o traçado. ESC cancela.',
    'wms.map.trace_summary': 'Traçado',
    'wms.map.trace_cells': 'células',
    'wms.map.rack_draft_title': 'Novo rack',
    'wms.map.rack_place_title': 'Posicionar rack no mapa',
    'wms.map.rack_draft_hint':
        'O traçado (orientação e comprimento) veio dos dois cliques — para mudar, clique no mapa de novo.',
    'wms.map.rack_real_note':
        'Criar o rack aqui gera os endereços REAIS (blocos, andares e posições) junto com o desenho.',
    'wms.map.create_rack': 'Criar rack',
    'wms.map.place_rack': 'Posicionar no mapa',
    'wms.map.rack_prefix': 'Prefixo do rack (1–2 letras)',
    'wms.map.rack_name': 'Nome do rack',
    'wms.map.bays_count': 'Quantos blocos',
    'wms.map.bays_from_registry': 'blocos no cadastro',
    'wms.map.bay_side': 'Blocos ficam de que lado do corredor',
    'wms.map.ground_level_no_weight':
        'A1 é o térreo: o pallet apoia no chão e não tem limite de peso — os pesos começam no A2.',
    // Exclusão de rack — SÓ no editor (pede senha via ConfirmPasswordModal).
    // 409 lista os ocupantes; 404 é auto-cura (some do desenho sem erro
    // assustador). Não existe mais "lado" (ímpar/par) a excluir ou adicionar.
    'wms.map.delete_rack': 'Excluir rack',
    'wms.map.delete_rack_hint':
        'Apaga os endereços reais do rack (só se estiver vazio) e tira do mapa. Pede senha.',
    'wms.map.rack_deleted': 'Rack excluído — endereços reais apagados.',
    'wms.map.rack_already_gone': 'Esse rack já não existe — removido do mapa.',
    'wms.map.delete_occupied': 'Ocupada — esvazie (mova os LPNs e libere as reservas) antes de excluir.',
    // Editar UM BLOCO individual (nº sequencial): excluir na ficha do bloco;
    // adicionar clicando num GAP do corredor. Pede senha; 409 lista ocupantes;
    // 404 é auto-cura.
    'wms.map.delete_block': 'Excluir bloco',
    'wms.map.delete_block_hint':
        'Apaga só ESTE bloco (só se estiver vazio) — vira um gap no rack. Pede senha.',
    'wms.map.block_deleted': 'Bloco excluído — endereços reais apagados.',
    'wms.map.block_already_gone': 'Esse bloco já não existe — removido do mapa.',
    'wms.map.block_added': 'Bloco adicionado — endereços reais gerados.',
    'wms.map.block_exists': 'Esse bloco já existe neste rack.',
    'wms.map.add_block': 'Adicionar bloco',
    'wms.map.add_block_title': 'Adicionar bloco',
    'wms.map.add_block_hint':
        'Preenche este gap do corredor: gera os endereços reais do bloco (andares × posições) com os andares/largura do rack. Pede senha.',
    'wms.map.area_type': 'Tipo da área',
    'wms.map.area_type.logistica': 'Logística',
    'wms.map.area_type.doca': 'Doca',
    'wms.map.area_type.staging': 'Staging',
    'wms.map.area_type.producao': 'Produção',
    'wms.map.area_type.buffer': 'Buffer',
    'wms.map.area_type.outro': 'Outro',
    // Orientação virou LEITURA (r5): sai do traçado de dois cliques — aparece
    // no resumo do painel do rack e na ficha do rack, nunca como escolha.
    'wms.map.orientation_label': 'Orientação',
    'wms.map.orientation_vertical': 'Vertical',
    'wms.map.orientation_horizontal': 'Horizontal',
    // De que lado do CORREDOR ficam os blocos do rack — geometria do desenho
    // (nunca paridade): vertical = esquerda/direita; horizontal = acima (fundo,
    // y cresce) / abaixo (saída).
    'wms.map.side.left': 'Esquerda',
    'wms.map.side.right': 'Direita',
    'wms.map.side.top': 'Acima (fundo)',
    'wms.map.side.bottom': 'Abaixo (saída)',
    'wms.map.levels': 'Andares por bloco',
    'wms.map.max_levels': 'Máximo de andares',
    'wms.map.level_height': 'Altura do andar (m)',
    'wms.map.positions': 'Posições por andar',
    'wms.map.block_width': 'Largura do bloco (m)',
    'wms.map.grid_w': 'Grid X',
    'wms.map.grid_h': 'Grid Y',
    'wms.map.grid_cell': 'Célula (px)',
    'wms.map.area_title': 'Área',
    'wms.map.rack_title': 'Rack',
    'wms.map.block_title': 'Bloco',
    'wms.map.delete_area': 'Excluir área',
    'wms.map.area_edit_note':
        'Para mover, arraste a área no mapa; para expandir ou reduzir, selecione-a e arraste as alças da borda. Racks e blocos precisam ficar dentro de Logística, Doca ou Produção; staging pode ficar dentro de uma área (processo do setor) ou solto (transferência entre setores).',
    'wms.map.rack_edit_note':
        'Para estender ou reduzir, selecione o rack e arraste a alça no fim dele. Para mudar o rack de lugar, arraste o rack inteiro no mapa.',
    'wms.map.container_area': 'Área container',
    'wms.map.blocks_count': 'Blocos',
    'wms.map.click_select_rack': 'clique para selecionar o rack',
    'wms.map.no_block_data': 'sem cadastro para este bloco',
    'wms.map.fixed_hover': 'endereço fixo — clique para ver os detalhes',
    'wms.map.place_fixed_hint': 'clique numa célula livre para posicionar no mapa; ESC cancela.',
    'wms.map.fixed_placed': 'Endereço posicionado no mapa.',

    // Salvar com re-senha (Editor de galpão, r3)
    'wms.map.confirm_password_title': 'Confirme sua senha',
    'wms.map.confirm_password_hint':
        'Esta ação mexe nos endereços reais do galpão (salvar o layout, criar ou excluir um rack ou um bloco). Confirme a senha do seu usuário para continuar.',
    'wms.map.password': 'Senha',
    'wms.map.password_wrong': 'Senha incorreta.',
    'wms.map.confirm_and_save': 'Confirmar',

    // Impressão USB pessoal via QZ Tray (4ª rodada — cadastro global é SÓ
    // rede; USB é da máquina de quem imprime, listada na hora pelo QZ)
    'wms.labels.qz_not_running':
        'QZ Tray fora do ar — instale/abra o QZ Tray neste computador (https://qz.io): é por ele que saem as etiquetas (USB e rede).',
    'wms.printer.no_host': 'Impressora de rede sem host cadastrado.',
    'wms.printer.not_loaded': 'A lista de impressoras ainda não carregou — tente de novo.',
    'wms.labels.network_group': 'Rede (cadastradas)',
    'wms.labels.network_none': 'Nenhuma impressora de rede ativa',
    'wms.labels.usb_group': 'Impressora USB (QZ Tray)',
    'wms.labels.usb_loading': 'Procurando impressoras locais…',
    'wms.labels.usb_none': 'Nenhuma impressora local encontrada',

    // Coletor
    'wms.collector.title': 'Coletor',
    'wms.collector.subtitle': 'Armazém',
    'wms.collector.identify_title': 'Identifique-se',
    'wms.collector.identify_hint': 'Escaneie seu crachá ou digite o ID do funcionário.',
    'wms.collector.code_placeholder': 'ID do funcionário',
    'wms.collector.confirm': 'Confirmar',
    'wms.collector.operator': 'Funcionário',
    'wms.collector.change_operator': 'Deslogar',
    'wms.collector.not_identified': 'Não identificado',
    'wms.collector.device': 'Dispositivo',
    // `wms.collector.ready_title`/`ready_hint` saíram no M4: o texto prometia
    // que "os fluxos guiados chegam nos próximos módulos" e o espaço dele na
    // tela virou justamente os dois botões de ação. Se ainda existirem linhas
    // dessas chaves em `wms_label_texts`, são órfãs e podem sair do seed.
    'wms.collector.error_empty': 'Escaneie o crachá ou digite o ID do funcionário.',
    'wms.collector.error_not_found': 'ID não encontrado ou funcionário inativo.',
    'wms.collector.error_network': 'Sem conexão com o servidor. Verifique a rede e tente novamente.',

    // Impressoras & etiquetas ZPL (M1 r2) — chaves entram AQUI (agente B)
    'wms.tab.printers': 'Impressoras',
    'wms.printers.title': 'Impressoras',
    'wms.printers.description':
        'Impressoras de etiqueta (ZPL) que ficam NA REDE, para todo mundo enxergar sem precisar procurar. O envio é direto por TCP (porta 9100). Impressora USB não se cadastra aqui: ela é do computador de cada um e aparece na hora de imprimir, no grupo "Impressora USB".',
    'wms.printer.field.host': 'Host (IP ou nome na rede)',
    'wms.printer.field.port': 'Porta',
    'wms.printer.field.host_port': 'Host:porta',
    'wms.printer.port_help': 'Vazio usa a porta padrão de impressoras de etiqueta (9100).',
    'wms.printer.test': 'Testar',
    'wms.printer.test_ok': 'Impressora respondeu',
    'wms.printer.test_fail': 'Sem resposta da impressora',
    'wms.printer.discover': 'Detectar na rede',
    'wms.printer.discover_description':
        'O QZ Tray deste computador varre a rede local (/24) atrás de impressoras com a porta 9100 aberta. A faixa vem do IP daqui; troque se as impressoras estiverem em outra.',
    'wms.printer.discover_base_ip': 'Base da rede (3 octetos)',
    'wms.printer.discover_scan': 'Varrer',
    'wms.printer.discover_scanning': 'Varrendo a rede — pode levar alguns segundos…',
    'wms.printer.discover_none': 'Nenhuma impressora encontrada nessa faixa.',
    'wms.printer.discover_register': '+ cadastrar',
    'wms.printer.discover_invalid_base': 'Informe os 3 primeiros octetos — ex.: 192.168.0',

    // Etiquetas ZPL (modal de etiquetas v2 — bloco / endereço específico)
    'wms.labels.zpl_title': 'Etiquetas (ZPL)',
    'wms.labels.zpl_description':
        'Gera a etiqueta em ZPL: de BLOCO (a única etiqueta física do rack — QR com Rack+Bloco, ex.: A-01) ou de um ENDEREÇO específico (staging, doca, fábrica, incidente — QR com o código literal).',
    'wms.labels.scope_block': 'Etiqueta de bloco',
    'wms.labels.scope_block_help':
        'QR com Rack+Bloco (prova de presença no rack). Andar e posição não têm etiqueta — são escolhidos na torre do bloco.',
    'wms.labels.scope_address': 'Endereço específico',
    'wms.labels.scope_address_help': 'Staging, doca, fábrica ou incidente — o QR carrega o código literal.',
    'wms.labels.code_placeholder': 'ex.: DCA-801',
    'wms.labels.generate_preview': 'Gerar prévia',
    'wms.labels.preview_title': 'Prévia da etiqueta',
    'wms.labels.scan_code': 'Código do QR',
    'wms.labels.send': 'Enviar para impressora',
    'wms.labels.sent': 'Etiqueta enviada para a impressora',
    'wms.labels.download': 'Baixar .zpl',
    'wms.labels.printer': 'Impressora',
    'wms.labels.select_printer': 'Selecione a impressora…',
    'wms.labels.no_printers':
        'Nenhuma impressora de rede cadastrada — cadastre em Configurações do Armazém → Impressoras. As Impressora USB seguem disponíveis no seletor (QZ Tray).',
    'wms.labels.block_required': 'Informe o bloco (a etiqueta é por bloco).',

    // M10 — mídia da etiqueta e impressão em lote dos blocos
    'wms.labels.media': 'Tamanho da etiqueta',
    'wms.labels.media_help':
        'A 40x60 não é a grande encolhida: ela sai só com o QR e o código embaixo. O QR fica com ~26 mm — ótimo para o coletor de mão, curto para ler da empilhadeira a 5 m.',
    'wms.labels.media_100x150': '100x150 — título grande + QR (leitura à distância)',
    'wms.labels.media_40x60': '40x60 — QR + código (etiqueta pequena)',
    'wms.labels.media_40x40': '40x40 — QR + código',
    'wms.labels.scope_street': 'Rack inteiro',
    'wms.labels.scope_street_help':
        'Gera a etiqueta de TODOS os blocos do rack de uma vez, MAIS 1 etiqueta de staging da cabeceira por bloco (todas apontam para o mesmo STG do rack) — é o que se usa para etiquetar o galpão do zero.',
    'wms.labels.scope_street_hint': 'Deixe em "todos os racks" para etiquetar o galpão inteiro.',
    'wms.labels.all_streets': 'Todos os racks',
    'wms.labels.no_racks': 'Nenhum rack gerado',
    'wms.labels.rack_required': 'Escolha o rack.',
    'wms.labels.batch_preview_title': 'Prévia do lote',
    'wms.labels.batch_count': '{count} etiqueta(s) de bloco',
    'wms.labels.batch_empty': 'Nenhum bloco com posição viva — gere o rack antes de imprimir as etiquetas.',

    // M10 — inventário inicial (coletor): etiquetas em branco
    'wms.intake.action': 'Entrada de estoque',
    'wms.intake.step_lpn': 'Etiqueta',
    'wms.intake.step_product': 'Produto',
    'wms.intake.step_qty': 'Quantidade',
    'wms.intake.step_items': 'Itens',
    'wms.intake.step_block': 'Posição',
    'wms.intake.step_position': 'Posição',
    'wms.intake.title_lpn': 'Bipe a etiqueta em branco',
    'wms.intake.title_product': 'Bipe o código de barras do produto',
    'wms.intake.title_pick_product': 'Qual destes você tem na mão?',
    'wms.intake.title_qty': 'Quantas unidades?',
    'wms.intake.title_items': 'O que tem neste LPN?',
    'wms.intake.title_block': 'Bipe a etiqueta do bloco (ex.: A-01) ou do endereço (doca/staging)',
    'wms.intake.title_position': 'Toque na posição onde vai ficar',
    'wms.intake.add_another': 'Adicionar outro item',
    'wms.intake.go_to_position': 'Ir para a posição',
    'wms.intake.remove_item': 'Remover item',
    'wms.intake.product_hint': 'Vale o EAN da embalagem ou o SKU.',
    'wms.intake.qty': 'Quantidade',
    'wms.intake.qty_invalid': 'Quantidade inválida',
    'wms.intake.qty_invalid_help': 'Informe um número maior que zero.',
    'wms.intake.not_an_lpn': 'Isto não é uma etiqueta de pallet',
    'wms.intake.not_an_lpn_help': 'Pegue uma etiqueta em branco (ex.: MP-260814-001).',
    'wms.intake.already_used': 'Esta etiqueta já foi usada',
    'wms.intake.already_used_help':
        'Este pallet já tem conteúdo declarado. Pegue a próxima etiqueta em branco — ou use Ajustar quantidade, se a intenção era corrigir o que já está lá.',
    'wms.intake.not_a_block': 'Isto não é um bloco nem uma posição',
    'wms.intake.not_a_block_help':
        'Bipe o bloco do rack (ex.: A-01) para escolher a posição na torre, ou uma posição não-rack (doca, staging) para colocar direto ali.',
    'wms.intake.product_not_found': 'Produto não encontrado',
    'wms.intake.product_not_found_help': 'Nenhum produto do catálogo tem este EAN nem este SKU.',
    'wms.intake.ambiguous_help':
        'Este código de barras está cadastrado em mais de um produto — normalmente a UNIDADE e a CAIXA. Escolha o que está na sua mão: contar a caixa como unidade erra o inventário em várias vezes.',
    'wms.intake.save_failed': 'Não deu para gravar',
    'wms.intake.done_title': 'Entrada registrada',
    'wms.intake.stored_lpn': 'Pallet',
    'wms.intake.next_label': 'Próxima etiqueta',

    // M10 — etiquetas em branco (console do Supervisor)
    'wms.lpn_batch.title': 'Imprimir LPNs',
    'wms.lpn_batch.action': 'Imprimir LPNs',
    'wms.lpn_batch.description':
        'Gera N etiquetas de pallet já numeradas. Por padrão saem EM BRANCO (papel na mão: não contam como estoque nem ocupam posição) e o operador preenche cada uma no coletor. Opcionalmente, já defina a ORIGEM e os ITENS do lote aqui — o mesmo conteúdo vai em todos, e vira estoque quando o coletor coloca cada LPN numa posição.',
    'wms.lpn_batch.with_origin': 'Definir origem (opcional)',
    'wms.lpn_batch.origin_help': 'De onde a carga veio — vale para todos os LPNs do lote.',
    'wms.lpn_batch.with_items': 'Declarar itens (opcional)',
    'wms.lpn_batch.items_help':
        'O MESMO conteúdo vai em todos os LPNs do lote. Não é estoque ainda — vira saldo quando o coletor coloca o LPN numa posição.',
    'wms.lpn_batch.qty': 'Quantas etiquetas',
    'wms.lpn_batch.qty_help': 'De 1 a 300. Lembre que a numeração é de 999 por dia por prefixo.',
    'wms.lpn_batch.notes': 'Observação (opcional)',
    'wms.lpn_batch.generate': 'Gerar e imprimir',
    'wms.lpn_batch.generated': '{count} etiqueta(s) geradas',
    'wms.lpn_batch.used': '{used} de {total} já viraram pallet',
    'wms.lpn_batch.reprint': 'Reimprimir',
    'wms.lpn_batch.reprint_unused': 'Só as não usadas',
    'wms.lpn_batch.empty': 'Nenhuma etiqueta gerada ainda.',
    'wms.lpn_batch.created_at': 'Gerado em',

    // Genéricos do módulo
    'wms.common.new': 'Novo',
    'wms.common.edit': 'Editar',
    'wms.common.activate': 'Ativar',
    'wms.common.deactivate': 'Desativar',
    'wms.common.status': 'Status',
    'wms.common.actions': 'Ações',
    'wms.common.active': 'Ativo',
    'wms.common.inactive': 'Inativo',
    'wms.common.save': 'Salvar',
    'wms.common.cancel': 'Cancelar',
    'wms.common.close': 'Fechar',
    'wms.common.required': 'Campo obrigatório',
    'wms.common.invalid_json': 'JSON inválido — ex.: 1.4, true, "FROM_DOCK" ou {"a": 1}.',
    'wms.common.conflict': 'Este código já está em uso por um registro ativo.',
    'wms.common.load_error': 'Não foi possível carregar os dados. O backend pode não estar no ar.',
    'wms.common.retry': 'Tentar Novamente',

    // Localizações r3 (página) — feedback do dono 07/08 (M1 3ª rodada).
    // Enums novos do contrato
    'wms.location_status.MAINTENANCE': 'Manutenção',
    'wms.location_type.VIRTUAL': 'Virtual',
    'wms.field.occupancy': 'Ocupação',
    'wms.occupancy.FREE': 'Livre',
    'wms.occupancy.OCCUPIED': 'Ocupada',
    'wms.occupancy.RESERVED': 'Reservada (em trânsito)',
    // Filtro de área (chips) — trava por permissão entra com os papéis do M4/M5
    'wms.locations.area_filter': 'Área',
    'wms.locations.area_all': 'Todas',
    'wms.locations.area_virtual': 'Virtuais',
    'wms.locations.area_locked': 'Seu acesso é restrito a esta área.',
    // Alternância de formato da listagem
    'wms.locations.view_list': 'Lista',
    'wms.locations.view_cascade': 'Cascata',
    // Vínculo lista↔mapa
    'wms.locations.map_column': 'Mapa',
    'wms.locations.see_on_map': 'Ver no mapa',
    'wms.locations.virtual_off_map': 'Virtual — fora do mapa',
    // Minimapa r3 (página)
    'wms.map.open_editor': 'Editor de galpão',
    'wms.map.status_hint':
        'Clique num rack para filtrar a listagem; clique num bloco para ver o que tem nele e o que está chegando.',
    'wms.map.virtual_filter_note':
        'Localizações virtuais não aparecem no mapa — são endereços de inventário fora do galpão (armários, salas). Use a listagem abaixo.',
    'wms.map.legend_has_maintenance': 'Contém manutenção',
    // Cascata (estilo pastas) e lista agrupada
    'wms.cascade.virtual_group': 'Virtuais',
    'wms.cascade.fixed_group': 'Endereços fixos',
    'wms.cascade.no_sector': 'Sem setor',
    'wms.cascade.bay': 'vão',
    'wms.cascade.position_suffix': 'posição',
    'wms.cascade.positions_suffix': 'posições',
    'wms.cascade.empty': 'Nenhuma localização para os filtros atuais.',
    // Painel "O que tem aqui / O que está chegando"
    'wms.panel.whats_here': 'O que tem aqui',
    'wms.panel.incoming': 'O que está chegando',
    'wms.panel.whats_here_empty': 'Nada por aqui ainda.',
    'wms.panel.incoming_empty': 'Nenhuma OS a caminho.',
    'wms.panel.block_positions': 'Posições do bloco',
    'wms.panel.details': 'Detalhes',
    // Nova localização (fluxo r3: primeiro pergunta se é virtual)
    'wms.location_create.is_virtual_question': 'É virtual?',
    'wms.location_create.virtual_option': 'Virtual',
    'wms.location_create.virtual_option_help':
        'Endereço de inventário fora do galpão (armário de TI, marketing, comercial…). Não aparece no mapa.',
    'wms.location_create.physical_option': 'Física',
    'wms.location_create.physical_option_help':
        'Endereço do galpão — doca, staging de doca, fábrica ou incidente. Posições de rack nascem pelo Editar galpão (criar rack).',
    'wms.location_create.virtual_code_help':
        'Código livre — vira o token bipável, único por organização (ex.: TI-ARMARIO-01).',
    'wms.location_create.created_title': 'Localização criada',
    'wms.location_create.place_on_map': 'Posicionar no minimapa',
    'wms.location_create.place_on_map_hint':
        'Abre o editor de galpão para ajustar o desenho. O posicionamento fino por localização chega junto com as permissões dedicadas.',
    'wms.location_create.finish': 'Concluir',
    'wms.location_create.back': 'Voltar',
    // Edição em massa v2 (matriz rack × blocos × andares)
    'wms.bulk.description_v2':
        'Aplica UM ajuste à matriz rack × blocos × andares: status, peso máximo ou largura do vão. Vazio em blocos/andares aplica a todos.',
    'wms.bulk.blocks_help': 'Números e faixas: 1,3,5-9 — vazio aplica a todos os blocos.',
    'wms.bulk.levels_help': 'Números e faixas: 1,3-5 — vazio aplica a todos os andares.',
    'wms.bulk.width_is_per_block':
        'A largura do vão é do BLOCO (vale para todas as posições dele, em todos os andares) — por isso Andares fica desabilitado.',
    'wms.bulk.invalid_list': 'Lista inválida — use números e faixas separados por vírgula (ex.: 1,3,5-9).',
    // Edição individual (largura saiu: é do bloco)
    'wms.edit.bay_width_readonly': 'A largura do vão é do bloco — ajuste pela Edição em massa.',

    // M2 — Regras de depósito & Produtos
    'wms.tab.deposit_rules': 'Regras de depósito',
    'wms.deposit_rules.title': 'Regras de depósito',
    'wms.deposit_rules.description':
        'Transferência automática de depósito por evento (§6.3): quando o evento acontece, o LPN é transferido AUTOMATICAMENTE para o depósito configurado. A transferência é CONTÁBIL (muda o depósito do LPN e registra DEPOSIT_TRANSFER) — depósito nunca bloqueia posição física. Regra não configurada = nunca definida; para trocar, edite (não existe exclusão). O disparo entra com o motor do M4/M5.',
    'wms.deposit_rules.event': 'Evento',
    'wms.deposit_rules.target': 'Depósito destino',
    'wms.deposit_rules.not_configured': '— não configurada',
    'wms.deposit_rules.deposit_help': 'Somente depósitos ATIVOS aparecem aqui.',
    'wms.deposit_rules.deposit_inactive_suffix': '(inativo)',
    'wms.deposit_rules.select_deposit': 'Selecione o depósito…',
    'wms.deposit_rules.no_deposits': 'Nenhum depósito ativo — cadastre ou reative um na aba Depósitos.',
    'wms.deposit_rules.saved': 'Regra de depósito atualizada',
    'wms.deposit_event.PRODUCTION_OUTPUT': 'Saída de produção',
    'wms.deposit_event.FACTORY_IN_ENTRY': 'Entrada na fábrica',
    'wms.deposit_event.INCIDENT_ENTRY': 'Entrada em incidente',
    'wms.deposit_event.CUSTOMER_RETURN_RECEIPT': 'Recebimento de devolução',
    'wms.deposit_event.CUSTOMER_RETURN_SELLABLE': 'Devolução triada — volta ao vendável',
    'wms.deposit_event.RMA_SEND': 'Envio a fornecedor (RMA)',
    // Aba Depósitos — hint de replicação (seed do M2 nasce com mirror desligado)
    'wms.deposits.bling_hint':
        'Os depósitos do seed nascem com a replicação para o Bling DESLIGADA — para ligar, edite o depósito e informe o ID do depósito no Bling.',
    // Produtos (catálogo Bling existente + atributos locais do WMS)
    'wms.tab.products': 'Produtos',
    'wms.products.title': 'Produtos',
    'wms.products.description':
        'Catálogo de produtos já espelhado do Bling (sync existente do melidash) com os atributos locais do WMS. Os interruptores salvam na hora — clique para ligar/desligar.',
    'wms.products.count': 'produtos',
    'wms.products.empty': 'Nenhum produto encontrado.',
    'wms.products.search_placeholder': 'Buscar por SKU ou nome…',
    'wms.field.sku': 'SKU',
    'wms.products.is_bulky': 'Volumoso',
    'wms.products.is_bulky_help': 'Exige vão de 2,50 m (trava passiva na alocação).',
    'wms.products.track_supplier_lot': 'Rastreia lote do fornecedor',
    'wms.products.track_supplier_lot_help': 'Bipagem do lote da bobina-mãe na produção.',

    // Localizações r4 — UNIFICAÇÃO da página (feedback do dono 10/08):
    // mapa e dados são UMA coisa; drawer permanente; modo Tabela|Cascata|Mapa.
    'wms.locations.mode_table': 'Tabela',
    'wms.locations.mode_cascade': 'Cascata',
    'wms.locations.mode_map': 'Mapa',
    // Drawer lateral permanente (coluna fixa da página)
    'wms.panel.empty_title': 'Nenhuma posição selecionada',
    'wms.panel.empty_hint': 'Clique numa posição — na tabela, na cascata ou no mapa — para ver os detalhes aqui.',
    'wms.panel.clear': 'Limpar seleção',
    'wms.panel.rack_summary': 'Posições do rack',
    'wms.panel.selected_rack': 'Rack selecionado',
    'wms.panel.code_not_loaded':
        'Endereço fora da listagem atual — limpe os filtros ou recarregue a página para ver os detalhes.',
    // Mapa dentro da página (modo Mapa + edição inline)
    'wms.map.edit_warehouse': 'Editar galpão',
    'wms.map.finish_edit': 'Concluir edição',
    'wms.map.edit_inline_hint':
        'Edição direta no mapa: arraste da paleta para criar; salvar re-pede a sua senha. Concluir a edição sem salvar descarta as alterações.',
    'wms.map.not_configured_r4':
        'O mapa do galpão ainda não foi desenhado. Use "Editar galpão" para desenhar as áreas e os racks — toda localização física nasce posicionada no mapa.',
    // Posicionamento obrigatório da nova localização física (criar rack é
    // só no Editar galpão — a página não tem fluxo de rack)
    'wms.place.fixed_banner': 'Posicionando no mapa:',
    'wms.place.fixed_hint': 'Clique na célula do mapa onde este endereço fica. Cancelar não cria a localização.',
    'wms.place.cancel': 'Cancelar posicionamento',
    'wms.place.cancelled_not_created': 'Posicionamento cancelado — a localização NÃO foi criada.',
    'wms.place.created_and_placed': 'Localização criada e posicionada no mapa.',
    'wms.place.create_failed_after_place':
        'A célula foi desenhada no mapa, mas a criação do endereço falhou — remova a célula no Editar galpão ou tente de novo.',
    // Nova localização (fluxo r4: física exige posicionar no mapa)
    'wms.location_create.physical_place_step':
        'Ao continuar, a página abre o mapa para você clicar na célula onde este endereço fica. A localização só é criada depois do posicionamento.',
    'wms.location_create.continue_to_map': 'Posicionar no mapa',
    // Configurações — abas por permissão
    'wms.config.no_tabs_title': 'Sem acesso às configurações do armazém',
    'wms.config.no_tabs_hint':
        'Seu usuário não tem permissão para nenhuma aba desta página (wms:manage:*). Peça o acesso a um administrador.',

    // ─────────────────────────────────────────────────────────────────────────
    // M3 — LPNs (LPN & Recebimento). Página /warehouse/lpns, criação com
    // etiqueta na hora e a ficha "onde está". Terminologia: LPN (nunca
    // "palete", nunca "caixa") e "posição" para o endereço físico.
    // ─────────────────────────────────────────────────────────────────────────
    'wms.tab.lpns': 'LPNs',
    'wms.lpns.title': 'LPNs',
    'wms.lpns.description':
        'Unidades logísticas do armazém: cada LPN tem um código próprio, uma posição, um depósito e os itens que carrega — é nos itens que o saldo vive. Receba mercadoria criando o LPN (a etiqueta sai na hora), consulte onde cada um está e reimprima a etiqueta quando precisar.',
    'wms.lpns.receive': 'Receber LPN',
    'wms.lpns.count': 'LPNs',
    'wms.lpns.empty': 'Nenhum LPN para os filtros atuais.',
    'wms.lpns.search_placeholder': 'Buscar pelo código do LPN…',
    'wms.lpns.status_all': 'Todos os status',
    'wms.lpns.location_all': 'Todas as posições',
    'wms.lpns.product_filter': 'Produto',
    'wms.lpns.filter_by_location': 'Filtrar por esta posição',
    'wms.lpns.location_filter': 'Filtrar posições',
    'wms.lpns.location_filter_placeholder': 'ex.: A-01',
    'wms.lpns.location_more': 'Refine o filtro — posições fora da lista:',
    'wms.lpns.no_locations': 'Nenhuma posição ativa cadastrada.',
    'wms.lpns.clear_product': 'Trocar o produto',
    'wms.lpns.searching': 'Buscando no catálogo…',
    'wms.lpns.where_is_it': 'Onde está',
    'wms.lpns.items_empty': 'Este LPN não tem itens.',
    'wms.lpns.not_found': 'LPN não encontrado.',
    'wms.lpns.reprint': 'Reimprimir etiqueta',
    'wms.lpns.no_zpl': 'Etiqueta indisponível para este LPN — fale com o Supervisor.',
    'wms.lpns.panel_empty_title': 'Nenhum LPN selecionado',
    'wms.lpns.panel_empty_hint':
        'Clique num LPN da lista para ver onde ele está, o que tem dentro e reimprimir a etiqueta.',

    // Campos do LPN
    'wms.lpn.field.location': 'Posição',
    'wms.lpn.field.deposit': 'Depósito',
    'wms.lpn.field.items': 'Itens',
    'wms.lpn.field.item_count': 'Itens',
    'wms.lpn.field.is_bulky': 'Volumoso',
    'wms.lpn.field.created_at': 'Criado em',
    'wms.lpn.field.origin': 'Origem',
    'wms.lpn.field.qty': 'Quantidade',
    'wms.lpn.field.supplier_lot': 'Lote do fornecedor',
    'wms.lpn.field.expiry_date': 'Validade',

    // Enums do LPN
    'wms.lpn_status.ACTIVE': 'Ativo',
    'wms.lpn_status.CLOSED': 'Fechado',
    'wms.lpn_origin.PURCHASE_ORDER': 'Ordem de compra',
    'wms.lpn_origin.PRODUCTION_ORDER': 'Ordem de produção',
    'wms.lpn_origin.RETURN': 'Devolução',
    'wms.lpn_origin.LEGACY': 'Avulso (legado)',
    'wms.lpn_origin.REPACK': 'Reorganização de LPN',

    // Recebimento (criação do LPN)
    'wms.lpn_create.title': 'Receber LPN',
    'wms.lpn_create.description':
        'O código do LPN é gerado pelo sistema a partir do prefixo (a sequência reinicia a cada dia). Posição e depósito são obrigatórios; a origem liga o LPN à ordem de compra que o trouxe.',
    'wms.lpn_create.prefix': 'Prefixo',
    'wms.lpn_create.no_prefixes': 'Nenhum prefixo ativo — cadastre em Configurações do Armazém.',
    'wms.lpn_create.no_deposits': 'Nenhum depósito ativo — cadastre em Configurações do Armazém.',
    'wms.lpn_create.select_location': 'Selecione a posição…',
    'wms.lpn_create.no_origin': 'Sem origem',
    'wms.lpn_create.purchase_order': 'Ordem de compra',
    'wms.lpn_create.select_purchase_order': 'Selecione a ordem de compra…',
    'wms.lpn_create.origin_id': 'Identificação da origem',
    'wms.lpn_create.purchase_orders_unavailable':
        'Não foi possível listar as ordens de compra (o seu acesso pode não incluir Compras) — informe a identificação da origem à mão.',
    'wms.lpn_create.is_bulky_help':
        'Vem marcado quando algum produto do LPN é volumoso (exige vão de 2,50 m). Corrija se a carga real for diferente.',
    'wms.lpn_create.items_empty': 'Nenhum item ainda — busque o produto abaixo e adicione.',
    'wms.lpn_create.add_product': 'Produto',
    'wms.lpn_create.add_item': 'Adicionar item',
    'wms.lpn_create.remove_item': 'Remover item',
    'wms.lpn_create.pick_product': 'Escolha o produto na busca do catálogo.',
    'wms.lpn_create.qty_invalid': 'Informe uma quantidade maior que zero.',
    'wms.lpn_create.items_required': 'Adicione ao menos um item — o saldo do LPN vive nos itens.',
    'wms.lpn_create.submit': 'Criar LPN',
    'wms.lpn_create.created_title': 'LPN criado',
    'wms.lpn_create.print_hint': 'Imprima a etiqueta e cole no LPN antes de movimentar.',
    'wms.lpn_create.finish': 'Concluir',

    // Drawer do M1 ("O que tem aqui" virou dado real no M3)
    'wms.panel.no_lpns_here': 'Nenhum LPN nesta posição.',
    // O que o LPN carrega são PRODUTOS. "itens" ao lado do código do pallet se
    // lia como contagem de LPN ("1 itens" num lugar que só comporta 1 pallet).
    'wms.panel.lpn_products_suffix_one': 'produto',
    'wms.panel.lpn_products_suffix': 'produtos',
    // Duas etiquetas no mesmo vão: dado legado (§6.2), não operação normal.
    'wms.panel.lpns_conflict': 'LPNs no mesmo vão',
    'wms.panel.whats_here_block': 'Clique numa posição do bloco para ver os LPNs dela.',

    // ─────────────────────────────────────────────────────────────────────
    // M4 — Coletor & incidentes
    //
    // Vocabulário do chão: o bipe do pallet é sempre "LPN" (o protótipo
    // chamava de "série" — erro apontado no PRD §8.2). O título de cada passo
    // é o VERBO no imperativo ("Bipe a posição de origem"), o flash de sucesso
    // é a confirmação no particípio ("Origem confirmada") e o botão diz o ato,
    // não o resultado. Frase curta: quem lê está em cima de uma empilhadeira.
    // ─────────────────────────────────────────────────────────────────────

    // Menu do coletor (as duas ações depois de identificar o operador)
    'wms.collector.equipment': 'Equipamento desta sessão',
    'wms.collector.equipment_none': 'Nenhum (movimento a pé)',
    'wms.collector.action_move': 'Mover pallet',
    'wms.collector.action_free_query': 'Consulta livre',
    'wms.collector.lpn_label': 'LPN',
    'wms.collector.back_to_menu': 'Voltar',
    'wms.collector.restart_flow': 'Recomeçar',

    // Entrada de leitura (HID oculto + digitação manual)
    'wms.collector.hid_input_aria': 'Leitura do coletor — bipe o código',
    'wms.collector.waiting_scan': 'Aguardando o bipe…',
    'wms.collector.scan_disabled': 'Leitura pausada',
    'wms.collector.manual_open': 'Digitar',
    'wms.collector.manual_title': 'Digite o código',
    'wms.collector.manual_placeholder': 'Código',
    'wms.collector.manual_confirm': 'Confirmar',
    'wms.collector.manual_typed_note': 'O envio digitado fica registrado como DIGITADO na trilha.',
    'wms.collector.scan_method': 'Leitura',
    'wms.collector.scan_method.SCANNED': 'Bipada',
    'wms.collector.scan_method.TYPED': 'Digitada',

    // Consulta livre
    'wms.collector.free_query_title': 'Consulta livre',
    'wms.collector.free_query_hint': 'Bipe uma posição, um bloco ou um LPN.',
    'wms.collector.free_query_idle': 'O resultado do bipe aparece aqui.',
    'wms.collector.is_a_location': 'Isto é uma posição',
    'wms.collector.is_a_block': 'Isto é um bloco',
    'wms.collector.is_a_lpn': 'Isto é um LPN',
    'wms.collector.lpns_here': 'LPNs nesta posição',
    'wms.collector.lpn_is_at': 'Está em',
    'wms.collector.lpn_no_location': 'Sem posição registrada',
    'wms.collector.unknown_title': 'Código não reconhecido',
    'wms.collector.unknown_hint': 'Não é posição, bloco nem LPN. Confira a etiqueta e bipe de novo.',

    // Fluxo guiado — passos
    'wms.collector.step': 'Passo',
    'wms.collector.back': 'Voltar',
    'wms.collector.error_generic': 'Não foi possível concluir. Confira e tente de novo.',
    'wms.collector.step_name.ORIGIN': 'Origem',
    'wms.collector.step_name.LPN': 'LPN',
    'wms.collector.step_name.UNITS': 'Unidades',
    'wms.collector.step_name.STAGING': 'Cabeceira',
    // O "00": desceu o pallet, encostou no chão da cabeceira, bipou.
    'wms.collector.go_to_staging': 'Desça o pallet e bipe a cabeceira do rack',
    'wms.collector.staging_hint': 'É a etiqueta do chão na ponta do rack (o "00").',
    'wms.collector.flash_staging': 'Pallet no chão',
    'wms.collector.mismatch_staging_title': 'Não é a cabeceira deste rack',
    'wms.collector.step_name.DESTINATION': 'Destino',
    'wms.collector.step_origin_title': 'Bipe a posição de ORIGEM',
    'wms.collector.step_origin_hint': 'A etiqueta física é a do BLOCO (ex.: A-01) — a posição você escolhe na torre.',
    'wms.collector.step_lpn_title': 'Bipe o LPN',
    'wms.collector.step_destination_title': 'Bipe o DESTINO',
    'wms.collector.step_destination_hint': 'Bipe a etiqueta do bloco (ex.: A-01) ou do endereço de destino.',
    'wms.collector.pick_origin_title': 'Qual posição deste bloco?',
    'wms.collector.pick_destination_title': 'Em qual posição vai guardar?',
    'wms.collector.expected': 'Esperado',
    'wms.collector.expected_one_of': 'Esperado um destes LPNs:',
    'wms.collector.scan_pallet_here': 'Bipe a etiqueta do pallet desta posição',
    'wms.collector.take_to': 'Leve para',
    'wms.collector.expected_any_location': 'Uma posição ou um bloco',
    'wms.collector.expected_any_lpn': 'Um LPN',
    'wms.collector.read': 'Lido',
    'wms.collector.from': 'De',
    'wms.collector.to': 'Para',

    // Torre do bloco (andar × posição, alvo aceso)
    'wms.collector.tower_title': 'Torre do bloco',
    'wms.collector.tower_legend': 'P1 fica à direita de quem está de frente para o rack.',
    'wms.collector.tower_empty': 'Este bloco não tem posições cadastradas.',

    // Flashes de confirmação
    'wms.collector.flash_origin': 'Origem confirmada',
    'wms.collector.flash_block': 'Bloco confirmado',
    'wms.collector.flash_lpn': 'LPN confirmado',
    'wms.collector.flash_destination': 'Destino confirmado',

    // Telas vermelhas
    'wms.collector.mismatch_type_title': 'Não era isso',
    'wms.collector.mismatch_expected_location': 'Este passo espera uma POSIÇÃO ou um BLOCO.',
    'wms.collector.mismatch_expected_lpn': 'Este passo espera um LPN.',
    'wms.collector.mismatch_lpn_title': 'LPN errado',
    'wms.collector.mismatch_lpn_hint': 'Este LPN não é o que está nesta posição.',
    'wms.collector.mismatch_place_title': 'LPN está em outro lugar',
    'wms.collector.mismatch_place_hint': 'O sistema registra este LPN em outra posição.',
    'wms.collector.mismatch_destination_title': 'Destino errado',
    'wms.collector.mismatch_staging_hint': 'Leve o pallet ao staging da cabeceira do rack e bipe a etiqueta do chão.',
    'wms.collector.scan_failed_title': 'Não deu para ler',
    'wms.collector.try_again': 'Tentar de novo',
    'wms.collector.report_mismatch': 'Registrar incidente',
    'wms.collector.blocked_title': 'Não pode guardar aqui',
    'wms.collector.blocked_hint': 'A regra do armazém barrou este destino:',
    'wms.collector.pick_other_destination': 'Escolher outro destino',

    // Conferência e conclusão
    'wms.collector.confirm_title': 'Confira antes de gravar',
    'wms.collector.confirm_move': 'Confirmar movimento',
    'wms.collector.move_done_title': 'Movimento gravado',
    'wms.collector.new_movement': 'Novo movimento',

    // Exceções do chão (§6.2 e §6.5)
    'wms.collector.register_occupied': 'Registrar ocupado',
    'wms.collector.occupied_note': 'Posição livre no sistema e ocupada no chão:',
    'wms.collector.occupied_no_street': 'A origem não está num rack — não dá para desviar para o staging da cabeceira.',
    'wms.collector.redirected_to_staging': 'Ocupação registrada. Leve o pallet ao staging da rua:',
    'wms.collector.no_lpn_here_title': 'Nenhum LPN nesta posição',
    'wms.collector.no_lpn_here_hint': 'O sistema não registra pallet aqui. Se tem pallet no chão, registre o incidente.',
    'wms.collector.report_missing_pallet': 'Registrar pallet ausente',
    'wms.collector.missing_pallet_note': 'Posição sem o pallet esperado:',
    'wms.collector.incident_created': 'Incidente registrado — o Supervisor foi avisado.',

    // Painel de incidentes (Supervisor)
    'wms.incidents.title': 'Incidentes do Armazém',
    'wms.incidents.description':
        'Divergências abertas do chão pelo coletor: ocupação não cadastrada, pallet ausente e bipe fora do esperado. Resolver é registrar o que foi feito — o incidente fica na trilha.',
    'wms.incidents.count_label': 'incidentes',
    'wms.incidents.search_placeholder': 'Pesquisar por posição, LPN ou nota…',
    'wms.incidents.empty_open': 'Nenhum incidente aberto. O chão e o sistema estão de acordo.',
    'wms.incidents.empty_resolved': 'Nenhum incidente resolvido ainda.',
    'wms.incidents.field.kind': 'Tipo',
    'wms.incidents.field.location': 'Posição',
    'wms.incidents.field.lpn': 'LPN',
    'wms.incidents.field.created_by': 'Aberto por',
    'wms.incidents.field.created_at': 'Quando',
    // A antiga coluna "Notas" virou duas coisas com nomes próprios na seção do
    // histórico, no fim deste arquivo: `field.report` (o relato de abertura) e
    // `field.notes_count` (quantas anotações o caso tem).
    'wms.incidents.resolve': 'Resolver',
    'wms.incidents.resolve_title': 'Resolver incidente',
    'wms.incidents.resolve_notes': 'O que foi feito',
    'wms.incidents.resolve_notes_placeholder': 'Ex.: criado o LPN legado PA-260810-031 na posição.',
    'wms.incidents.resolve_hint': 'Resolver não apaga o incidente — ele fica no histórico com quem resolveu e quando.',
    'wms.incidents.resolved_toast': 'Incidente resolvido.',

    'wms.incident_status.OPEN': 'Aberto',
    'wms.incident_status.RESOLVED': 'Resolvido',
    'wms.incident_kind.SCAN_MISMATCH': 'Bipe divergente',
    'wms.incident_kind.OCCUPIED_DESTINATION': 'Destino ocupado',
    'wms.incident_kind.MISSING_PALLET': 'Pallet ausente',
    'wms.incident_kind.WO_CANCELED_WITH_LOAD': 'OS cancelada com carga',
    'wms.incident_kind.UNREGISTERED_OCCUPANCY': 'Ocupação não cadastrada',
    'wms.incident_kind.OTHER': 'Outro',

    // ── Histórico do incidente (ajuste do aceite do M4) ─────────────────────
    // Palavras do dono: "usuário poderia adicionar quantas notas ele quiser até
    // chegar ao momento de marcar como resolvido... histórico do incidente
    // completo: quando começou, quem reportou, histórico de anotações até dar
    // como resolvido". Daí o vocabulário desta seção separar três coisas que
    // antes cabiam todas na palavra "Notas": o RELATO de abertura (o que foi
    // encontrado, escrito uma vez), as ANOTAÇÕES (a condução do caso, uma por
    // linha, com autor e hora) e a NOTA FINAL (o que foi feito para resolver).
    'wms.incidents.field.status': 'Status',
    'wms.incidents.field.notes_count': 'Anotações',
    'wms.incidents.field.resolved_by': 'Resolvido por',
    'wms.incidents.field.resolved_at': 'Resolvido em',
    'wms.incidents.field.report': 'Relato de abertura',

    'wms.incidents.detail_title': 'Histórico do incidente',
    'wms.incidents.detail_hint': 'Clique em uma linha para abrir o histórico completo do incidente.',
    'wms.incidents.open_detail': 'Ver histórico',
    'wms.incidents.detail_error': 'Não foi possível carregar o histórico do incidente.',

    'wms.incidents.timeline_title': 'Linha do tempo',
    'wms.incidents.timeline_opened': 'Incidente aberto',
    'wms.incidents.timeline_resolved': 'Incidente resolvido',
    'wms.incidents.timeline_pending': 'Em aberto — aguardando resolução.',
    'wms.incidents.timeline_no_report': 'Aberto sem texto de relato.',
    'wms.incidents.timeline_note': 'Anotação',
    'wms.incidents.timeline_empty': 'Nenhuma anotação até agora — só a abertura.',

    'wms.incidents.add_note': 'Adicionar anotação',
    'wms.incidents.add_note_placeholder': 'Ex.: subi na posição com a empilhadeira; o pallet é de matéria-prima.',
    'wms.incidents.add_note_hint':
        'A anotação entra no histórico com seu nome e a hora — e não pode ser editada nem apagada depois.',
    'wms.incidents.note_added_toast': 'Anotação registrada no histórico.',
    'wms.incidents.notes_closed': 'Incidente resolvido: o histórico está fechado. Reabra o incidente para anotar.',

    'wms.incidents.resolve_notes_required': 'Escreva o que foi feito para poder resolver.',
    'wms.incidents.reopen': 'Reabrir',
    'wms.incidents.reopened_toast': 'Incidente reaberto.',
    // ── UM campo + UM seletor (11/08) ───────────────────────────────────────
    // A escolha vem ANTES de escrever, e não depois (era o botão apertado que
    // decidia o destino do texto). "O que fazer" é a pergunta que o seletor
    // responde — "Ações" descrevia uma barra de botões que não existe mais.
    'wms.incidents.action_label': 'O que fazer',
    'wms.incidents.add_note_option': 'Adicionar como anotação',
    // Reabrir EXIGE justificativa (400 no backend sem ela): o campo tem que
    // dizer isso pelo nome, não só recusar depois de digitado.
    'wms.incidents.reopen_notes': 'Motivo da reabertura',
    'wms.incidents.reopen_notes_required': 'Explique por que está reabrindo.',

    // ── Rótulos dos PARÂMETROS (aba Parâmetros) ─────────────────────────────
    // A aba edita QUALQUER chave de `wms_settings` (é agnóstica de propósito),
    // mas mostrar só `speed_mps` cru não diz nada ao dono. Estes rótulos são
    // fallback local do dicionário `wms.setting.<chave>` servido pelo backend —
    // chave sem rótulo cadastrado continua aparecendo pelo próprio nome, o que
    // mantém a aba funcionando para parâmetros que o dono criar pela UI.
    'wms.setting.speed_mps': 'Velocidade de deslocamento (m/s)',
    'wms.setting.lift_up_sec_per_level': 'Içar por andar (s)',
    'wms.setting.lift_down_sec_per_level': 'Descer por andar (s)',
    'wms.setting.handling_base_sec': 'Manuseio base (s)',
    'wms.setting.height_factor_per_level': 'Fator de altura por andar',
    'wms.setting.side_turn_sec': 'Giro de lado (s)',
    'wms.setting.p2_extra_sec': 'Extra P2 (s)',
    'wms.setting.map_orientation': 'Orientação do mapa',
    // Trava (c) do §6.1 parametrizada (ajuste do dono no aceite do M4): é o
    // admin que informa o maior vão que a empresa permite para carga volumosa.
    'wms.setting.bulky_min_bay_width': 'Vão mínimo para carga volumosa (m)',
    // ⚠️ As 4 medidas em metros (travessa frontal, distância entre ruas,
    // profundidade de bloco, aproximação lateral) MORRERAM em 11/08: eram uma
    // SEGUNDA descrição do galpão, paralela ao mapa que o dono já desenhou.
    // Ficou UMA: quantos metros vale uma célula do mapa — o único número que o
    // desenho não tem como saber sozinho, e o que transforma o desenho em tempo.
    'wms.setting.map_cell_m': 'Quanto vale uma célula do mapa (m)',
    'wms.settings.geometry_notice':
        'As distâncias e os tempos do comparador saem do MAPA do galpão. Confira quantos metros vale uma célula — é esse número que transforma o desenho em minutos.',
    'wms.settings.unlabeled': 'Sem rótulo cadastrado',

    // ── INVENTÁRIO, OCUPAÇÃO e CONTAGEM (M9) ────────────────────────────────
    // O painel só vale se o número reconciliar com a trilha — por isso a aba de
    // CONCILIAÇÃO tem nome próprio e não é um rodapé de outra tela.    'wms.tab.inventory': 'Inventário',
    'wms.tab.occupancy': 'Ocupação',
    'wms.tab.aging': 'FIFO / Idade',
    'wms.tab.inventory': 'Inventário',
    'wms.tab.sla': 'SLA',
    'wms.tab.reconciliation': 'Conciliação',

    // O kind novo do enum (a decisão declarada do M9).
    'wms.incident_kind.COUNT_DIVERGENCE': 'Divergência de contagem',

    // ── Heatmap e espaço ocioso (§8.1.3 + §5) ───────────────────────────────
    'wms.heatmap.title': 'Ocupação por bloco',
    'wms.heatmap.legend': 'Termômetro: verde vazio → vermelho cheio',
    'wms.heatmap.band.EMPTY': 'Vazio',
    'wms.heatmap.band.LOW': 'Pouco ocupado',
    'wms.heatmap.band.MEDIUM': 'Meio ocupado',
    'wms.heatmap.band.HIGH': 'Quase cheio',
    'wms.heatmap.band.FULL': 'Cheio',
    'wms.heatmap.band.UNAVAILABLE': 'Fora de operação',
    'wms.heatmap.blocked_hatch': 'Hachurado = posição bloqueada (o andar 5 não é alcançável)',
    // A frase que explica a regra mais difícil do módulo, na tela onde ela age.
    'wms.heatmap.blocked_rule':
        'O andar 5 fica FORA do denominador de capacidade (a empilhadeira não alcança) e DENTRO do espaço ocioso — é o custo de não ter o equipamento maior.',

    'wms.occupancy.total_positions': 'Posições cadastradas',
    'wms.occupancy.capacity_positions': 'Capacidade disponível',
    'wms.occupancy.occupied_positions': 'Posições ocupadas',
    'wms.occupancy.free_positions': 'Posições livres',
    'wms.occupancy.blocked_positions': 'Posições bloqueadas',
    'wms.occupancy.occupancy_pct': '% ocupado',
    'wms.occupancy.idle_positions': 'Espaço parado',
    'wms.occupancy.idle_available': 'Parado e utilizável',
    'wms.occupancy.idle_blocked': 'Parado por bloqueio (A5)',
    // Indicadores de custo — visão diretoria (frente inventory, 25/08).
    'wms.occupancy.cost_title': 'Custo do espaço (rateio por posição)',
    'wms.occupancy.cua': 'Custo por posição (CUA/TPP)',
    'wms.occupancy.cost_occupied': 'Ocupação eficiente',
    'wms.occupancy.cost_idle': 'Capacidade ociosa',
    'wms.occupancy.cost_blocked': 'Indisponibilidade operacional',
    'wms.occupancy.cor': 'Ociosidade relativa (COR)',
    'wms.occupancy.cor_hint': 'ocioso ÷ custo total',
    'wms.occupancy.idle_cost': 'Custo do espaço parado (mês)',
    'wms.occupancy.idle_blocked_cost': 'Custo do andar bloqueado (mês)',
    'wms.occupancy.cost_missing':
        'Custo mensal do galpão não configurado — cadastre "warehouse_monthly_cost_brl" nos Parâmetros para ver o valor em reais.',
    'wms.occupancy.by_level': 'Ocupação por andar',

    // ── FIFO / aging (§6.2) ─────────────────────────────────────────────────
    'wms.aging.title': 'Idade dos pallets (FIFO)',
    'wms.aging.bucket': 'Faixa (dias)',
    'wms.aging.age_days': 'Idade (dias)',
    'wms.aging.oldest': 'Pallet mais antigo',
    'wms.aging.average_age': 'Idade média',
    'wms.aging.warning_only': 'FIFO é AVISO: o sistema mostra a ordem, nunca bloqueia a retirada.',
    'wms.aging.total_qty': 'Quantidade',
    'wms.aging.empty': 'Nenhum pallet ativo',

    // ── SLA (§6.5) ──────────────────────────────────────────────────────────
    'wms.sla.title': 'SLA de execução',
    'wms.sla.dimension.OPERATOR': 'Por operador',
    'wms.sla.dimension.EQUIPMENT': 'Por equipamento',
    'wms.sla.dimension.TYPE': 'Por tipo de OS',
    'wms.sla.orders': 'OSs',
    'wms.sla.measured': 'Medidas',
    'wms.sla.execution_avg': 'Execução média',
    'wms.sla.execution_p50': 'Mediana',
    'wms.sla.execution_p90': 'p90',
    'wms.sla.execution_max': 'Pior caso',
    'wms.sla.paused': 'Tempo pausado',
    'wms.sla.canceled': 'Canceladas',
    'wms.sla.skipped_items': 'Itens pulados',
    'wms.sla.pauses_discounted': 'O tempo de execução já desconta as pausas. OS cancelada não entra na média.',
    'wms.sla.no_group': 'Sem atribuição',
    'wms.sla.empty': 'Nenhuma OS no período',

    // ── Conciliação (o DoD) ─────────────────────────────────────────────────
    'wms.reconciliation.title': 'Conciliação com a trilha',
    'wms.reconciliation.hint':
        'Cada linha compara o SALDO de agora com a soma dos movimentos do mesmo pallet. Zero divergências é a prova de que o painel não inventa número.',
    'wms.reconciliation.holder': 'Pallet / volume',
    'wms.reconciliation.balance': 'Saldo',
    'wms.reconciliation.trail': 'Trilha',
    'wms.reconciliation.difference': 'Diferença',
    'wms.reconciliation.checked': 'Linhas conferidas',
    'wms.reconciliation.mismatched': 'Divergentes',
    'wms.reconciliation.balanced': 'Tudo bate com a trilha',
    'wms.reconciliation.only_mismatched': 'Só as divergentes',
    'wms.reconciliation.wip_note':
        'O WIP da fábrica não é endereçável pela trilha (o consumo aponta o pallet de origem, e a OP mora em lot_consumptions) — ele fica declarado de fora, nunca somado por dedução.',

    // ── Contagem (§6.9) ─────────────────────────────────────────────────────
    'wms.count.title': 'Contagem',
    'wms.count.new': 'Nova contagem',
    'wms.count.pick_blocks': 'Escolha os blocos para contar',
    'wms.count.picked_blocks': 'Blocos escolhidos',
    'wms.count.blocked_excluded': 'Posições bloqueadas não entram na contagem — o andar 5 não é alcançável.',
    'wms.count.step_location': 'Bipe a posição',
    'wms.count.wrong_location_title': 'Posição errada',
    'wms.count.next_position': 'Próxima posição',
    'wms.count.failed_title': 'Não foi possível gravar a contagem',
    'wms.count.step_lpn': 'Bipe o pallet (LPN)',
    'wms.count.step_qty': 'Confirme ou ajuste a quantidade',
    'wms.count.expected_qty': 'Esperado',
    'wms.count.counted_qty': 'Contado',
    'wms.count.difference': 'Diferença',
    'wms.count.confirm_all': 'Confirmar tudo como está',
    'wms.count.finish_position': 'Concluir posição',
    'wms.count.nothing_here': 'Não há nada nesta posição',
    'wms.count.expected_here': 'O sistema espera aqui',
    'wms.count.outcome.MATCH': 'Confere',
    'wms.count.outcome.DIVERGENCE': 'Divergência',
    'wms.count.outcome.MISSING': 'Não encontrado',
    'wms.count.outcome.FOREIGN': 'Pallet de outro lugar',
    'wms.count.divergence_hint':
        'Divergência abre incidente e a contagem SEGUE — o item não trava a rota. O ajuste grava o movimento com a diferença.',
    'wms.count.adjusted': 'Saldo ajustado pela contagem',
    'wms.count.not_adjusted': 'Saldo NÃO alterado — o Supervisor decide pelo incidente',
    'wms.count.empty': 'Nenhuma contagem registrada',

    // ── EXPEDIÇÃO e ESPELHO BLING (M8) ──────────────────────────────────────
    // A trava de 100% e a fila do espelho. Dicionário ÚNICO: console e coletor
    // falam do MESMO checklist (CLAUDE.md §4).    // ── Página / navegação ──────────────────────────────────────────────────
    'wms.shipping.title': 'Expedição',
    'wms.shipping.description':
        'Carregamento dos romaneios: o checklist do que deve embarcar, o bipe de cada pallet/volume na doca e o fechamento — que baixa o estoque e avisa o Bling. Nada fecha com item não bipado.',
    'wms.tab.shipments': 'Embarques',
    'wms.tab.bling_mirror': 'Espelho Bling',

    // ── Lista de embarques ──────────────────────────────────────────────────
    'wms.shipment.count': 'embarques',
    'wms.shipment.empty': 'Nenhum embarque. Abra o carregamento de um romaneio para começar.',
    'wms.shipment.open_action': 'Abrir embarque',
    'wms.shipment.status_all': 'Todos os status',
    'wms.shipment.select_hint': 'Escolha um embarque na lista para ver o checklist.',
    'wms.shipment.refresh': 'Atualizar',

    // ── Enums ───────────────────────────────────────────────────────────────
    'wms.shipment_status.OPEN': 'Carregando',
    'wms.shipment_status.CLOSED': 'Fechado',
    'wms.shipment_status.CANCELED': 'Cancelado',
    'wms.shipment_item_kind.LPN': 'Pallet',
    'wms.shipment_item_kind.VOLUME': 'Volume',
    'wms.shipment_item_status.PENDING': 'Falta bipar',
    'wms.shipment_item_status.LOADED': 'No veículo',

    // ── Campos ──────────────────────────────────────────────────────────────
    'wms.shipment.field.outbound': 'Romaneio',
    'wms.shipment.field.dock': 'Doca',
    'wms.shipment.field.progress': 'Carregamento',
    'wms.shipment.field.status': 'Status',
    'wms.shipment.field.created_at': 'Aberto em',
    'wms.shipment.field.closed_at': 'Fechado em',
    'wms.shipment.field.vehicle_plate': 'Placa',
    'wms.shipment.field.driver': 'Motorista',
    'wms.shipment.field.notes': 'Observações',

    // ── Checklist e a TRAVA DE 100% ─────────────────────────────────────────
    'wms.shipment.checklist_title': 'Checklist do romaneio',
    'wms.shipment.checklist_hint':
        'O que deve embarcar × o que já foi bipado na doca. Cada linha vira "no veículo" quando o operador bipa a etiqueta dela E a etiqueta da doca.',
    'wms.shipment.missing_title': 'Falta bipar',
    'wms.shipment.missing_none': 'Tudo bipado. O embarque pode ser fechado.',
    'wms.shipment.loaded_title': 'Já no veículo',
    'wms.shipment.loaded_none': 'Nada foi bipado ainda.',
    'wms.shipment.progress_of': '{loaded} de {total} bipados',
    'wms.shipment.empty_checklist':
        'O checklist está vazio: não há pallet nem volume para carregar. Acrescente o que vai embarcar antes de fechar.',
    'wms.shipment.close_blocked_title': 'Ainda não dá para fechar',
    'wms.shipment.add_lpns': 'Acrescentar pallets',
    'wms.shipment.add_lpns_hint': 'Um código de LPN por linha. Volumes do romaneio entram sozinhos.',
    'wms.shipment.remove_item': 'Remover do checklist',
    'wms.shipment.remove_item_confirm': 'Remover {code} do checklist deste embarque?',
    'wms.shipment.remove_item_loaded': 'Item já bipado não sai do checklist.',
    'wms.shipment.scanned_by': 'Bipado por',

    // ── Abrir embarque ──────────────────────────────────────────────────────
    'wms.shipment.open_title': 'Abrir embarque de um romaneio',
    'wms.shipment.open_hint':
        'O embarque é a sessão de carregamento de um romaneio que já existe. Nada aqui cria romaneio, motorista ou placa.',
    'wms.shipment.outbound_picker': 'Romaneio',
    'wms.shipment.outbound_empty': 'Nenhum romaneio disponível para carregamento.',
    'wms.shipment.outbound_forbidden':
        'Sua conta não tem permissão para listar romaneios (wms:read). Informe o identificador do romaneio — ele está na URL da página do romaneio (/wms/outbound/<id>).',
    'wms.shipment.outbound_id_manual': 'Identificador do romaneio',
    'wms.shipment.dock_picker': 'Doca do embarque',
    // ⚠️ REESCRITO em 13/08: o texto anterior dizia que o operador bipava esta
    // etiqueta para confirmar a carga no veículo. Isso deixou de ser verdade —
    // o dono tirou o bipe da doca do carregamento ("só quando for tirado do
    // estoque e levado para a Doca é que precisa bipar"). A doca do embarque
    // agora é só ONDE O CAMINHÃO ESTÁ, escolhida aqui no console.
    'wms.shipment.dock_hint': 'Onde o caminhão está encostado. É por ela que o coletor reconhece este embarque.',
    'wms.shipment.dock_forbidden': 'Sua conta não lista endereços — digite o código da doca (ex.: DCA-801).',
    'wms.shipment.lpn_codes': 'Pallets que vão embarcar (opcional)',
    'wms.shipment.open_confirm': 'Abrir embarque',

    // ── Fechar embarque ─────────────────────────────────────────────────────
    'wms.shipment.close': 'Fechar embarque',
    'wms.shipment.close_title': 'Fechar embarque',
    'wms.shipment.close_effects': 'O fechamento acontece numa transação só',
    'wms.shipment.close_effect_writeoff': 'baixa o estoque de tudo que foi carregado (WRITEOFF);',
    'wms.shipment.close_effect_lpn': 'fecha os pallets e os volumes do checklist;',
    'wms.shipment.close_effect_deposit': 'aplica a transferência de depósito configurada;',
    'wms.shipment.close_effect_bling': 'enfileira o espelho do Bling.',
    'wms.shipment.close_media': 'Mídia do carregamento',
    'wms.shipment.close_media_hint':
        'Fotos e vídeos vão para o ROMANEIO pelo módulo de mídia que já existe (até 10 arquivos: imagens/PDF de 10 MB, vídeos de 500 MB).',
    'wms.shipment.close_confirm': 'Fechar embarque',
    'wms.shipment.close_changed_title': 'O checklist mudou desde que esta tela carregou',
    'wms.shipment.close_changed_body':
        'A trava de 100% é refeita no servidor no instante do fechamento. Entrou carga nova no checklist — veja o que falta e bipe antes de fechar.',
    'wms.shipment.close_media_kept':
        'Os {count} arquivo(s) enviados JÁ ficaram anexados ao romaneio — a mídia sobe antes da transação de fechamento. Não reenvie os mesmos.',
    'wms.shipment.close_not_open_title': 'Este embarque não está mais aberto',
    'wms.shipment.close_not_open_body':
        'Ele já foi fechado ou cancelado — a baixa de estoque acontece uma vez só. Atualize a lista para ver o estado atual.',
    'wms.shipment.close_reload': 'Atualizar checklist',
    'wms.shipment.closed_title': 'Embarque fechado',
    'wms.shipment.closed_lpns': 'pallets fechados',
    'wms.shipment.closed_volumes': 'volumes fechados',
    'wms.shipment.closed_writeoffs': 'baixas gravadas',
    // A fila passou a ser por PAR (produto, depósito) — o espelho virou em
    // pedaços, um depósito do melidash para um depósito do Bling.
    'wms.shipment.closed_bling': 'itens na fila do Bling (produto × depósito)',
    'wms.bling_mirror.field.deposit': 'Depósito',
    'wms.shipment.closed_deposit_rule': 'Transferência de depósito',
    'wms.shipment.closed_deposit_none': 'Sem regra configurada — nada a transferir.',
    'wms.shipment.closed_media': 'arquivos anexados ao romaneio',

    // ── Cancelar ────────────────────────────────────────────────────────────
    'wms.shipment.cancel': 'Cancelar embarque',
    'wms.shipment.cancel_title': 'Cancelar embarque',
    'wms.shipment.cancel_hint':
        'Cancelar NÃO é fechar sem bipar: não baixa estoque, não fecha pallet e não avisa o Bling. Serve para o embarque aberto no romaneio errado.',
    'wms.shipment.cancel_confirm': 'Cancelar embarque',

    // ── Fila do espelho Bling ───────────────────────────────────────────────
    'wms.bling_mirror.title': 'Fila do espelho Bling',
    'wms.bling_mirror.description':
        'O que o fechamento enfileirou para o Bling. O worker recalcula o saldo no instante do envio (a fila é marcador de sujeira, não retrato) e tenta de novo com espera crescente até desistir.',
    'wms.bling_mirror.count': 'linhas',
    'wms.bling_mirror.empty': 'Nada na fila.',
    'wms.bling_mirror.status_all': 'Todos os status',
    'wms.bling_sync_status.PENDING': 'Aguardando envio',
    'wms.bling_sync_status.SENT': 'Enviado',
    'wms.bling_sync_status.FAILED': 'Falhou',
    'wms.bling_mirror.field.product': 'Produto',
    'wms.bling_mirror.field.status': 'Status',
    'wms.bling_mirror.field.retries': 'Tentativas',
    'wms.bling_mirror.field.next_attempt': 'Próxima tentativa',
    'wms.bling_mirror.field.sent_at': 'Enviado em',
    'wms.bling_mirror.field.last_error': 'Motivo do erro',
    'wms.bling_mirror.field.created_at': 'Enfileirado em',
    'wms.bling_mirror.requeue': 'Reenfileirar falhas',
    'wms.bling_mirror.requeue_done': 'Linhas reenfileiradas.',
    'wms.bling_mirror.unavailable_title': 'A fila ainda não é exposta pela API',
    'wms.bling_mirror.unavailable_body':
        'A tabela bling_sync_queue e o worker existem e continuam rodando — o que falta é a rota de leitura (GET /api/v2/warehouse/bling-sync-queue). Enquanto ela não sobe, o motivo de um envio ter falhado só é visível no banco. Isto está relatado ao dono; esta tela passa a funcionar sozinha assim que a rota existir.',

    // ── Coletor (chão) ──────────────────────────────────────────────────────
    // O tipo SHIPPING de OS é a SEPARAÇÃO PARA ENVIO (o romaneio do WMS
    // antigo virou OS). "Carregar embarque" não existe mais como fluxo.
    'wms.service_order_type.SHIPPING': 'Separação para envio',
    'wms.shipment.collector.choose_title': 'Qual embarque você vai carregar?',
    'wms.shipment.collector.choose_empty': 'Nenhum embarque aberto. O Supervisor abre o embarque no console.',
    'wms.shipment.collector.step_name.ITEM': 'Carga',
    'wms.shipment.collector.step_item': 'Bipe o pallet ou o volume',
    'wms.shipment.collector.step_item_hint': 'A etiqueta do LPN, ou a etiqueta do ML no volume.',
    'wms.shipment.collector.flash_item': 'Carga lida',
    'wms.shipment.collector.flash_loaded': 'No veículo',
    'wms.shipment.collector.missing_title': 'Falta carregar',
    'wms.shipment.collector.missing_none': 'Tudo bipado. Avise o Supervisor para fechar o embarque.',
    'wms.shipment.collector.already_loaded_title': 'Este item já está no veículo',
    'wms.shipment.collector.already_loaded_body': 'Bipe repetido não muda nada e não gera movimento. Siga para o próximo.',
    'wms.shipment.collector.not_in_checklist_title': 'Esta carga não é deste romaneio',
    'wms.shipment.collector.not_in_checklist_body':
        'Não carregue: confira se a etiqueta é da carga deste caminhão. Se for, o Supervisor tem que acrescentá-la ao checklist.',
    'wms.shipment.collector.not_open_title': 'Este embarque não está mais aberto',
    'wms.shipment.collector.not_open_body': 'O Supervisor fechou ou cancelou o carregamento. Volte e escolha outro.',
    'wms.shipment.collector.done_title': 'Carregamento completo',
    'wms.shipment.collector.done_body': 'Tudo do romaneio foi bipado. O fechamento é do Supervisor, no console.',
    'wms.shipment.collector.change_shipment': 'Trocar de embarque',
    'wms.shipment.collector.dock_of': 'Doca deste embarque',

    // ── REPACK e PICKING FRACIONADO (M7) ────────────────────────────────────
    // Vocabulário do split/merge e do fracionado. Dicionário ÚNICO: o console
    // e o coletor falam do MESMO saldo, e dois vocabulários divergem no
    // primeiro texto que alguém ajusta só de um lado (CLAUDE.md §4).    // ── Página / console ────────────────────────────────────────────────────
    'wms.repack.title': 'REPACK & Picking fracionado',
    'wms.repack.subtitle':
        'Redistribuir o conteúdo dos pallets: dividir, juntar/transferir e separar fracionado. Todo saldo continua vivo em exatamente um lugar — um pallet, o consumo de uma OP ou um volume de expedição —, e a conta aparece na tela antes de qualquer gravação.',
    'wms.repack.tab.SPLIT': 'Dividir (1 → N)',
    'wms.repack.tab.MERGE': 'Juntar / transferir (N → 1)',
    'wms.repack.tab.PICK': 'Picking fracionado',
    'wms.repack.tab_hint.SPLIT': 'Parte do conteúdo de um pallet vai para outros — que já existem ou nascem agora.',
    'wms.repack.tab_hint.MERGE':
        'O conteúdo de um ou mais pallets vai para um só. Com uma origem só, isto é a transferência.',
    'wms.repack.tab_hint.PICK':
        'Separação fracionada: NÃO gera pallet. O saldo vai para o consumo de uma OP ou para um volume de expedição.',

    // ── Origem ──────────────────────────────────────────────────────────────
    'wms.repack.source_title': 'Pallet de origem',
    'wms.repack.sources_title': 'Pallets de origem',
    'wms.repack.source_hint': 'Bipe ou digite o código do LPN e carregue o conteúdo dele.',
    'wms.repack.lpn_code': 'Código do LPN',
    'wms.repack.load_lpn': 'Carregar',
    'wms.repack.change_lpn': 'Trocar pallet',
    'wms.repack.add_source': 'Adicionar outra origem',
    'wms.repack.remove_source': 'Remover esta origem',
    'wms.repack.no_items': 'Este pallet está sem conteúdo — não há o que mover.',
    'wms.repack.closed_lpn': 'Pallet FECHADO: não serve como origem nem como destino de um repack.',
    'wms.repack.source_forbidden':
        'Sem permissão para ler o conteúdo do pallet. Peça ao administrador o acesso de LPNs do armazém (wms:manage:lpns) — ele é irmão da permissão de repack, e não vem junto.',
    'wms.repack.qty_to_move': 'Quanto sai',
    'wms.repack.available': 'Tem no pallet',
    'wms.repack.nothing_to_move': 'Informe pelo menos uma quantidade maior que zero.',

    // ── Destinos ────────────────────────────────────────────────────────────
    'wms.repack.destinations_title': 'Destinos',
    'wms.repack.destination': 'Destino',
    'wms.repack.add_destination': 'Adicionar destino',
    'wms.repack.remove_destination': 'Remover este destino',
    'wms.repack.destination_mode': 'Este destino é',
    'wms.repack.destination_existing': 'Um pallet que já existe',
    'wms.repack.destination_new': 'Um pallet novo, criado agora',
    'wms.repack.destination_new_hint':
        'O código sai da sequência do dia (prefixo + data). A etiqueta é impressa no fim da operação, nesta mesma tela.',
    'wms.repack.destination_needs_code': 'Informe o código do pallet de destino.',
    'wms.repack.destination_needs_draft': 'Escolha prefixo, posição e depósito do pallet novo.',
    'wms.repack.new_lpn_forbidden_title': 'Criar pallet é ato do Supervisor',
    'wms.repack.new_lpn_forbidden_body':
        'Peça a etiqueta ao Supervisor, cole no pallet e informe o código — no chão, o destino já tem que existir.',
    'wms.repack.merge_target_title': 'Destino do merge',

    // ── Prévia / saldo ──────────────────────────────────────────────────────
    'wms.repack.preview': 'Ver saldo antes × depois',
    'wms.repack.preview_again': 'Refazer a conferência',
    'wms.repack.preview_required': 'Confira o saldo antes × depois para liberar a confirmação.',
    'wms.repack.preview_stale':
        'Esta conta vale para o formulário como ele está agora — qualquer edição descarta a conferência e pede outra.',
    'wms.repack.balance_title': 'Saldo antes × depois',
    'wms.repack.balance_hint':
        'Conta do servidor, sem gravar nada: é a MESMA que a confirmação vai executar. O navegador não recalcula saldo.',
    'wms.repack.balance_before': 'Antes',
    'wms.repack.balance_after': 'Depois',
    'wms.repack.balance_conserved': 'A conta fecha: a soma por produto é idêntica antes e depois.',
    'wms.repack.balance_broken': 'A conta NÃO fecha. Nada será gravado — avise o time do sistema.',
    'wms.repack.totals_title': 'Total por produto (a prova de que nada some nem aparece)',
    'wms.repack.holder.LPN': 'Pallet',
    'wms.repack.holder.PRODUCTION_ORDER': 'Consumo de OP (fábrica)',
    'wms.repack.holder.SHIPMENT_VOLUME': 'Volume de expedição',
    'wms.repack.will_close': 'Vai fechar',
    'wms.repack.will_close_hint':
        'O pallet fecha porque o conteúdo ZEROU — nunca pelo ato de desmembrar (§6.2). Dividir um pallet não o mata.',
    'wms.repack.will_create': 'Pallet que será criado',
    'wms.repack.without_lot': 'sem lote',
    'wms.repack.console_typed_note':
        'Operação feita pelo console: a trilha grava a leitura como DIGITADA (nada foi bipado aqui).',

    // ── FIFO (aviso, nunca trava) ───────────────────────────────────────────
    'wms.repack.fifo_title': 'FIFO: existe pallet mais antigo',
    'wms.repack.fifo_hint':
        'Aviso, não impedimento: dá para seguir. Existem motivos legítimos para furar a fila (o antigo está no aéreo, o cliente pediu o lote novo).',
    'wms.repack.fifo_show_candidates': 'Ver os pallets deste produto',
    'wms.repack.fifo_hide_candidates': 'Esconder a lista',
    'wms.repack.fifo_oldest': 'Mais antigo',
    'wms.repack.fifo_use': 'Usar este',
    'wms.repack.fifo_chosen': 'Escolhido',
    'wms.repack.fifo_empty': 'Nenhum outro pallet ativo com este produto.',

    // ── Picking fracionado ──────────────────────────────────────────────────
    'wms.repack.pick_no_lpn_title': 'O fracionado NÃO gera pallet',
    'wms.repack.pick_no_lpn_body':
        'Ele tira o saldo do pallet de origem e credita em UM destino: o consumo de uma OP (fábrica) ou um volume de expedição de um romaneio. Sem um dos dois o saldo sairia do sistema — e é isso que o servidor recusa.',
    'wms.repack.pick_destination_title': 'Para onde vai o que foi separado',
    'wms.repack.pick_destination_missing':
        'Falta escolher o destino: consumo de uma OP ou volume de um romaneio. Não é falha da operação — é a escolha que ainda não foi feita.',
    'wms.repack.pick_op': 'Consumo de OP (fábrica)',
    'wms.repack.pick_op_hint':
        'Um lote por vez — é o gesto da bancada do M6. O pallet precisa estar numa posição de entrada (FAB-*-IN) da OP.',
    'wms.repack.pick_op_field': 'Ordem de produção',
    'wms.repack.pick_op_single_line':
        'Fracionado para OP é UM lote por vez (o gesto da bancada): deixe quantidade em uma linha só.',
    'wms.repack.pick_op_id': 'ID da ordem de produção',
    'wms.repack.pick_op_unavailable':
        'Sem permissão para listar as ordens de produção — informe o ID da OP (o console da Fábrica mostra o ID).',
    'wms.repack.pick_op_empty': 'Nenhuma ordem de produção em andamento.',
    'wms.repack.pick_shipment': 'Volume de expedição (romaneio)',
    'wms.repack.pick_shipment_hint':
        'Flex/CrossDock: a etiqueta do Mercado Livre É o identificador do volume — não existe pallet interno para ele.',
    'wms.repack.pick_outbound_field': 'Romaneio',
    'wms.repack.pick_outbound_unavailable':
        'Sem permissão para listar os romaneios — informe o ID do romaneio de destino.',
    'wms.repack.pick_outbound_id': 'ID do romaneio',
    'wms.repack.pick_outbound_empty': 'Nenhum romaneio aberto.',
    'wms.repack.pick_volume_code': 'Etiqueta do volume (Mercado Livre)',
    'wms.repack.pick_volume_hint':
        'A etiqueta do ML É o identificador do volume — informe exatamente o que está colado na caixa. Volume que ainda não existe nasce agora, ligado ao romaneio escolhido.',

    // ── Confirmação / resultado ─────────────────────────────────────────────
    'wms.repack.confirm.SPLIT': 'Confirmar a divisão',
    'wms.repack.confirm.MERGE': 'Confirmar a junção',
    'wms.repack.confirm.PICK': 'Confirmar a separação',
    'wms.repack.done.SPLIT': 'Divisão gravada',
    'wms.repack.done.MERGE': 'Junção gravada',
    'wms.repack.done.PICK': 'Separação gravada',
    'wms.repack.done_hint': 'Saldo e trilha foram gravados na mesma transação.',
    'wms.repack.created_lpns': 'Pallets criados agora',
    'wms.repack.new_operation': 'Nova operação',
    'wms.repack.trail_kind': 'Trilha',
    'wms.repack.volume_result': 'Volume de expedição',

    // ── Coletor ─────────────────────────────────────────────────────────────
    'wms.repack.collector.action': 'Repack (dividir/juntar)',
    'wms.repack.collector.choose_title': 'O que você vai fazer?',
    'wms.repack.collector.split': 'Dividir pallet',
    'wms.repack.collector.split_hint': 'Tirar parte deste pallet e pôr em outro',
    'wms.repack.collector.merge': 'Juntar pallets',
    'wms.repack.collector.merge_hint': 'Passar o conteúdo deste pallet para outro',
    'wms.repack.collector.step_name.SOURCE': 'Origem',
    'wms.repack.collector.step_name.ITEM': 'O que sai',
    'wms.repack.collector.step_name.TARGET': 'Destino',
    'wms.repack.collector.step_name.QTY': 'Quantidade',
    'wms.repack.collector.step_source': 'Bipe o pallet de ORIGEM',
    'wms.repack.collector.step_source_hint': 'A etiqueta do pallet de onde a mercadoria vai sair.',
    'wms.repack.collector.step_item': 'O que vai sair deste pallet?',
    'wms.repack.collector.step_target': 'Bipe o pallet de DESTINO',
    'wms.repack.collector.step_target_hint': 'O pallet de destino já tem que estar etiquetado.',
    'wms.repack.collector.step_qty': 'Quanto vai?',
    'wms.repack.collector.move_all': 'Levar tudo',
    'wms.repack.collector.confirm': 'Confirmar',
    'wms.repack.collector.flash_source': 'Origem OK',
    'wms.repack.collector.flash_target': 'Destino OK',
    'wms.repack.collector.same_lpn_title': 'Origem e destino são o mesmo pallet',
    'wms.repack.collector.same_lpn_hint': 'Bipe outro pallet como destino.',
    'wms.repack.collector.forbidden_title': 'Peça o pallet ao Supervisor',
    'wms.repack.collector.forbidden_body':
        'Criar pallet é ato do Supervisor. Peça a etiqueta, cole no pallet e bipe o código — aqui no chão o destino já tem que existir.',
    'wms.repack.collector.done': 'Repack gravado',
    'wms.repack.collector.new_repack': 'Novo repack',
    'wms.repack.collector.refused_title': 'O servidor recusou',
    'wms.repack.collector.balance_line': 'Depois desta operação',

    // ── FÁBRICA (M6) ────────────────────────────────────────────────────────
    // Vocabulário da Ordem de Produção, da BANCADA e da genealogia. Vive aqui,
    // no dicionário ÚNICO do módulo, e não numa tabela paralela do M6: a
    // bancada e o console falam do MESMO lote de produção, e dois vocabulários
    // divergem no primeiro texto que alguém ajustar só de um lado.
    // Console (página /warehouse/production)
    'wms.production.title': 'Fábrica',
    'wms.production.description':
        'Ordens de produção, lotes (LT) e genealogia. A OP diz o que produzir e em quais posições de fábrica; na bancada, o operador escolhe uma OP liberada e aponta, no chão.',
    'wms.tab.production_orders': 'Ordens de produção',
    'wms.tab.production_lots': 'Lotes e genealogia',
    'wms.tab.machines': 'Máquinas',
    'wms.tab.boms': 'Ficha técnica (BOM)',
    'wms.tab.kit_options': 'Opções de kit',

    // Fábrica — chaves que a tela usava sem texto (apareciam cruas, "wms.field.machine").
    'wms.entity.production_order': 'Ordem de produção',
    'wms.entity.machine': 'Máquina',
    'wms.entity.bom': 'Ficha técnica',
    'wms.entity.kit_option': 'Opção de kit',
    'wms.field.production_order_code': 'OP',
    'wms.field.machine': 'Máquina',
    'wms.field.product_out': 'Produto de saída',
    'wms.field.product_in': 'Produto de entrada',
    'wms.field.kit_size': 'Unidades por kit',
    'wms.field.kits_count': 'Kits',
    'wms.field.qty_per_kit': 'Quantidade por kit',
    'wms.field.production_day': 'Dia de produção',
    'wms.field.production_lot_code': 'Lote (LT)',
    'wms.field.supplier_lot': 'Lote do fornecedor',
    'wms.field.scrap_qty': 'Refugo',
    'wms.field.theoretical_yield_pct': 'Rendimento teórico (%)',
    'wms.field.theoretical_scrap_pct': 'Refugo teórico (%)',
    'wms.production_order_status.PLANNED': 'Planejada',
    'wms.production_order_status.RUNNING': 'Liberada',
    'wms.production_order_status.DONE': 'Concluída',
    'wms.production_order_status.CANCELED': 'Cancelada',
    'wms.machine_material_status.RUNNING': 'Na máquina',
    'wms.machine_material_status.FINISHED': 'Encerrado',
    'wms.production.current_material': 'Material na máquina',
    'wms.production.no_material': 'Nenhum material capturado nesta máquina.',
    'wms.production.empty': 'Nenhuma ordem de produção encontrada.',
    'wms.production.genealogy': 'Genealogia',
    'wms.production.supplier_lots': 'Lotes do fornecedor',
    'wms.production.scrap_alert': 'Refugo acima do teórico',
    'wms.production.transition_kit': 'Kit de transição',
    'wms.action.capture_material': 'Capturar material',
    'wms.action.consume_material': 'Consumir material',
    'wms.action.finish_material': 'Encerrar material',
    'wms.action.produce_output': 'Apontar produção',
    'wms.action.print_kit_label': 'Imprimir etiqueta do kit',

    'wms.production.orders_title': 'Ordens de produção',
    'wms.production.putaway_service_order': 'Pallet {lpn}: OS {os} aberta para a logística.',
    'wms.production.putaway_already_scheduled': 'Pallet {lpn}: já está com a OS {os}.',
    'wms.production.putaway_released_empty': 'Pallet {lpn} não recebeu kit e voltou a ser etiqueta em branco.',
    'wms.production.putaway_already_stored': 'Pallet {lpn} já tinha saído da máquina (está em {local}): nada a buscar.',
    'wms.production.putaway_no_service_order_type': 'Atenção: não há tipo de OS de Transporte ativo. A OS do pallet {lpn} não saiu; avise o supervisor.',
    'wms.production.putaway_failed': 'Atenção: a OS do pallet {lpn} não saiu; avise o supervisor.',
    'wms.production.output_title': 'Pallets de saída',
    'wms.production.output_none': 'Nenhum pallet bipado nesta OP ainda.',
    'wms.production.output_state_current': 'Na bancada agora',
    'wms.production.output_state_current_forming': 'Na bancada agora, em formação (a saída tinha o anterior)',
    'wms.production.output_state_awaiting_pickup': 'Esperando a logística',
    'wms.production.output_state_at_factory': 'Na fábrica, sem OS',
    'wms.production.output_state_stored': 'Guardado',
    'wms.production.output_state_closed': 'Fechado',
    'wms.production.output_kits': '{kits} kits',
    'wms.production.output_os': 'OS de transporte',
    'wms.production.output_went_to': 'Foi para',
    'wms.production.output_now_at': 'Onde está agora',
    'wms.production.output_forming': 'Em formação na máquina',
    'wms.production.output_destination_pending': 'A logística escolhe ao guardar',
    'wms.production.machine_positions': 'Entrada e saída da máquina',
    'wms.production.machine_positions_free': 'A OP está livre: ela usa a entrada e a saída da máquina onde for aberta.',
    'wms.bench.notice_output_title': 'Saída da máquina ocupada',
    'wms.bench.notice_output_detail': 'A saída {location} está ocupada por {lpn}.',
    'wms.bench.notice_wait_logistics': 'Aguarde a logística fazer a coleta.',
    'wms.production.summary_title': 'Painel das OPs em aberto',
    'wms.production.summary_open': 'OPs em aberto',
    'wms.production.summary_open_split': '{planned} planejadas · {released} liberadas',
    'wms.production.summary_running': 'Rodando agora',
    'wms.production.summary_running_sub': 'OPs abertas numa máquina',
    'wms.production.summary_kits': 'Kits nas OPs em aberto',
    'wms.production.summary_no_target': 'Nenhuma OP aberta com meta de kits',
    'wms.production.summary_target': '{done} de {planned} kits da meta ({pct}%) em {orders} OPs com meta',
    'wms.production.summary_today': 'Kits apontados hoje',
    'wms.production.summary_today_sub': '{finished} OPs finalizadas hoje',
    'wms.production.under_construction': 'Em construção',
    'wms.production.under_construction_title': 'Esta área ainda está sendo adaptada à operação',
    'wms.production.under_construction_text':
        'Por enquanto a Fábrica opera com as Ordens de produção e as Máquinas. Esta aba volta quando estiver pronta para o chão.',
    'wms.production.orders_description':
        'Uma OP é produto de entrada e de saída + unidades por kit + as posições FAB-*-IN (entrada, só recebe o produto de entrada) e FAB-*-OT (saída) que ela usa — sem máquina: na bancada, o operador escolhe uma OP liberada e ela roda na máquina dele até ser pausada ou finalizada. O inventory_id que a bancada confere vem do anúncio do Full do produto de saída. Nasce Planejada; o supervisor a libera.',
    'wms.production.new_order': 'Nova OP',
    'wms.production.count': 'ordens de produção',
    'wms.production.search_placeholder': 'Buscar por código, produto ou máquina…',
    'wms.production.status_all': 'Todos os status',
    'wms.production.machine_all': 'Todas as máquinas',
    'wms.production.inputs': 'Entradas (IN)',
    'wms.production.outputs': 'Saídas (OT)',
    'wms.production.kit_size_help': 'Quantas unidades formam um kit (padrão 1). Cada bipe na bancada aponta essa quantidade.',
    'wms.production.product_in_missing': 'Não informado (OP antiga): cancele e crie de novo com o SKU de entrada.',
    'wms.production.kit_size_required': 'Informe as unidades por kit: um número inteiro, 1 ou mais.',
    'wms.production.product_in_help':
        'O SKU da matéria-prima. A entrada de fábrica da OP só aceita pallet com este SKU.',
    'wms.production.no_selection': 'Selecione uma ordem de produção para ver a ficha.',
    'wms.production.detail_title': 'Ficha da OP',
    'wms.production.lots_of_order': 'Lotes de produção (LT) desta OP',
    'wms.production.current_lot': 'Lote vigente',
    'wms.production.no_lots': 'Ainda sem lote — o LT nasce no primeiro consumo de material.',
    'wms.production.release': 'Liberar para produção',
    'wms.production.finish': 'Concluir OP',
    'wms.production.cancel': 'Cancelar OP',
    'wms.production.release_confirm':
        'Liberar a OP {code}? Liberada, ela aparece para qualquer bancada da Fábrica abrir.',
    'wms.production.cancel_confirm': 'Cancelar a OP {code}? Não há volta: DONE e CANCELED são estados finais.',
    'wms.production.finish_confirm': 'Concluir a OP {code}? DONE é estado final — a bancada não aponta mais nela.',
    'wms.production.no_machines':
        'Nenhuma máquina ativa cadastrada. Cadastre na aba Máquinas antes de abrir uma OP.',

    // Lotes e genealogia (recall)
    'wms.production.lots_title': 'Lotes de produção e genealogia',
    'wms.production.lots_description':
        'O LT é a identidade rastreável da saída: ele nasce no primeiro consumo, vira quando muda o dia de produção ou o lote do fornecedor, e é o que sai estampado na etiqueta do kit.',
    'wms.production.recall_title': 'Recall por lote do fornecedor',
    'wms.production.recall_hint':
        'Informe o lote impresso na etiqueta do fornecedor: a lista mostra todos os LTs que consumiram aquele lote — inclusive o kit de transição, que pertence a DOIS lotes e aparece pelos dois.',
    'wms.production.supplier_lot_filter': 'Lote do fornecedor',
    'wms.production.supplier_lot_placeholder': 'ex.: LOTE-A-2026',
    'wms.production.lots_count': 'lotes de produção',
    'wms.production.lots_empty': 'Nenhum lote de produção para este filtro.',
    'wms.production.lot_select': 'Selecione um lote para ver a genealogia.',
    'wms.production.consumptions': 'Consumos (o que entrou)',
    'wms.production.outputs_of_lot': 'Saídas (o que saiu com este LT)',
    'wms.production.no_consumptions': 'Sem consumos registrados.',
    'wms.production.no_outputs': 'Sem saídas apontadas neste lote ainda.',
    'wms.production.transition_lot_notice':
        'LOTE DE TRANSIÇÃO: este LT misturou {count} lotes de fornecedor ({lots}). O recall de qualquer um deles alcança as saídas abaixo.',
    'wms.production.source_supplier': 'Lote do fornecedor',
    'wms.production.source_production_lot': 'Lote fabricado aqui (desce mais um nível)',
    'wms.production.source_visited': 'Já mostrado acima nesta árvore',
    // ⚠️ REESCRITO em 11/08: o texto anterior ("a cadeia da v1 termina no lote do
    // fornecedor; a OC e o fornecedor vêm do módulo de Compras") era a DESCULPA
    // de quando a genealogia parava no meio. O dono mandou fechar o elo, e a
    // cadeia inteira já existia no dado — só não era percorrida.
    'wms.production.po_note':
        'A cadeia fecha no fornecedor: cada lote traz o pallet que o entregou e, quando a entrada veio de compra, a ordem de compra e o fornecedor.',
    // Genealogia até o fornecedor (nos dois sentidos)
    'wms.production.origin_chain': 'Origem da entrada',
    'wms.production.origin_purchase_order': 'Ordem de compra',
    'wms.production.origin_supplier': 'Fornecedor',
    'wms.production.origin_forward_title': 'De onde veio este lote',
    'wms.production.origin_forward_hint':
        'O caminho de volta do lote bipado na bobina: o pallet que o trouxe e, quando a entrada foi por compra, a ordem de compra e o fornecedor.',
    'wms.production.origin_forward_empty':
        'Nenhum pallet vinculado a este lote — ele entrou por bipe direto na bobina, sem pallet registrado.',
    // ⚠️ Um veredito por caso: origem que não é compra DIZ o que é. Em branco, o
    // leitor supõe "não rastreado"; fingindo compra, é pior — mente.
    'wms.lpn_origin_resolution.PURCHASE_ORDER': 'Compra',
    'wms.lpn_origin_resolution.PURCHASE_ORDER_NOT_FOUND': 'Ordem de compra não encontrada (vínculo órfão)',
    'wms.lpn_origin_resolution.OTHER_ORIGIN': 'Origem que não é compra',
    'wms.lpn_origin_resolution.NO_ORIGIN': 'Pallet sem origem cadastrada',
    'wms.lpn_origin_resolution.NO_LPN': 'Consumo sem pallet registrado (bipe direto do lote na bobina)',
    'wms.lpn_origin_resolution.LPN_NOT_FOUND': 'Pallet não encontrado no cadastro (registro antigo ou ajustado)',
    // Reimpressão da etiqueta do kit e montagem da OP
    'wms.action.reprint_kit_label': 'Reimprimir etiqueta',
    'wms.production.reprint_hint':
        'Segunda via idêntica à primeira: produto, lote e quantidade vêm do apontamento gravado.',
    'wms.production.origin_lpn': 'LPN de origem',
    'wms.production.tree_title': 'Árvore de origem',

    // Ficha técnica e opções de kit
    'wms.production.boms_title': 'Ficha técnica (BOM de rendimento)',
    'wms.production.boms_description':
        'Rendimento teórico e refugo aceitável por par mãe → filha. É contra o refugo teórico que o apontamento dispara ALERTA de desvio — alerta, nunca bloqueio.',
    'wms.production.boms_count': 'fichas técnicas',
    'wms.production.kit_options_title': 'Opções de kit',
    'wms.production.kit_options_description':
        'As combinações ALTERNATIVAS de um kit: um kit de 60 pode ser 6 peças de um componente OU 3 de outro. "Peças por kit" é quantas peças daquele componente formam um kit.',
    'wms.production.kit_options_count': 'opções de kit',
    'wms.production.kit_product': 'Kit (produto de saída)',
    'wms.production.component_product': 'Componente',
    'wms.production.recipe_empty': 'Nenhum registro. Cadastre o primeiro.',
    'wms.production.recipe_pick_products': 'Escolha os dois produtos.',
    'wms.production.machines_title': 'Máquinas',
    'wms.production.machines_description':
        'As máquinas da fábrica, cada uma casada com a SUA entrada (FAB-*-IN) e a SUA saída (FAB-*-OT): a OP usa as da máquina onde for aberta. Uma posição é de uma máquina só.',
    'wms.machine.input': 'Entrada (IN)',
    'wms.machine.output': 'Saída (OT)',
    'wms.machine.input_none': '— sem entrada —',
    'wms.machine.output_none': '— sem saída (a bancada não abre OP) —',

    // ── BANCADA (chão) ──────────────────────────────────────────────────────
    'wms.bench.title': 'Bancada de produção',
    'wms.bench.action': 'Bancada (produção)',
    // Estação de desktop da bancada (frente 2, 25/08).
    'wms.bench.station_title': 'Bancada',
    'wms.bench.no_fabrica_role': 'Este crachá não tem o papel de Fábrica. Bipe um crachá habilitado ou fale com o supervisor.',
    // Estação de bancada: Fábrica + Packing, com escolha (frente 3, 25/08).
    'wms.bench.station_title_fabrica': 'Bancada da Fábrica',
    // A bancada que EMBALA é o Packing; a que confere a carga do caminhão é o
    // CARREGAMENTO. Até 02/09 os dois nomes estavam trocados: "Packing" batizava
    // a conferência de carga (função que ninguém reconhecia) e o packing real
    // aparecia como "Embalar envios", em texto solto no JSX.
    'wms.bench.station_title_packing': 'Bancada de Packing',
    'wms.bench.station_title_loading': 'Bancada de Carregamento',
    'wms.bench.no_bench_role': 'Este crachá não faz Fábrica, Packing nem Carregamento. Bipe um crachá habilitado ou fale com o supervisor.',
    'wms.bench.choose_bench': 'Qual bancada você vai operar?',
    'wms.bench.switch_bench': 'Trocar bancada',
    'wms.bench.bench_fabrica': 'Fábrica',
    'wms.bench.bench_packing': 'Packing',
    'wms.bench.bench_packing_hint': 'bipar produto → embalar → etiqueta → fechar',
    'wms.bench.bench_loading': 'Carregamento',
    'wms.bench.bench_loading_hint': 'conferência da carga no caminhão',
    // A bancada vive em tela cheia (sem botão de alternar); a saída para o
    // sistema normal é o "Melidash" da tela do crachá, que abre o login em outra aba.
    'wms.bench.open_melidash': 'Melidash',
    'wms.bench.open_melidash_hint': 'Sai da tela cheia e abre o login do Melidash em outra aba',
    // Apontamento por bipe do inventory_id (frente 3): o kit pronto do Full traz
    // a etiqueta com o inventory_id (ex.: SITE31754), não o MLB.
    'wms.bench.scan_inventory_id': 'Bipe a etiqueta do Full (inventory_id) do kit pronto',
    'wms.bench.inventory_id_mismatch': 'Inventory ID não confere',
    // A bancada escolhe a OP (a OP não tem máquina): o estado de cada uma para ESTA bancada.
    'wms.bench.order_state_here': 'Aberta nesta máquina',
    'wms.bench.order_state_free': 'Liberada',
    'wms.bench.order_state_elsewhere': 'Rodando na {machine}',
    'wms.bench.order_state_planned': 'Planejada — aguardando liberação',
    'wms.bench.order_state_closed': 'Encerrada',
    'wms.bench.kits_target': 'Meta: {planned} kits',
    'wms.bench.pause_order': 'Pausar OP',
    'wms.bench.finish_order': 'Finalizar OP',
    'wms.bench.finish_order_confirm': 'Finalizar a OP {code}? Ela sai da bancada e não recebe mais kits.',
    'wms.bench.notice_back': 'Voltar às OPs',
    'wms.bench.notice_pick_machine': 'Escolher máquina',
    'wms.bench.notice_machine_title': 'A máquina {machine} não está mais com este terminal',
    'wms.bench.notice_machine_text':
        'Outro terminal assumiu a máquina, ou ela ficou mais de 2 minutos sem sinal daqui. A OP que estava aberta foi pausada: escolha a máquina de novo.',
    'wms.bench.machine_taken': 'Com {name}',
    'wms.bench.machine_here': 'Com este terminal',
    'wms.bench.machine_mine_elsewhere': 'Com você, em outro terminal',
    'wms.bench.notice_finished_title': 'OP {code} finalizada',
    'wms.bench.notice_target_title': 'Meta atingida: OP {code} finalizada',
    'wms.bench.notice_kits_text': '{kits} kits apontados. A OP saiu desta máquina.',
    'wms.bench.notice_paused_title': 'OP {code} pausada',
    'wms.bench.notice_paused_text': 'Ela ficou livre: esta ou qualquer outra máquina pode continuá-la.',
    'wms.bench.notice_left_title': 'A OP {code} não está mais nesta bancada',
    'wms.bench.notice_left_finished': 'Ela já foi finalizada.',
    'wms.bench.notice_left_finished_console': 'Ela foi finalizada pelo painel de produção.',
    'wms.bench.notice_left_canceled': 'Ela foi cancelada pelo painel de produção.',
    'wms.bench.notice_left_planned': 'Ela voltou a esperar a liberação do supervisor.',
    'wms.bench.notice_left_elsewhere': 'Ela agora está aberta na máquina {machine}.',
    'wms.bench.notice_left_removed':
        'O supervisor a tirou da bancada pelo painel de produção. Ela continua liberada e pode ser aberta de novo.',
    'wms.bench.notice_left_free': 'Ela foi pausada e está livre para ser aberta de novo.',
    'wms.bench.read': 'Bipado',
    'wms.bench.output_lpn': 'Pallet de saída (LPN)',
    'wms.bench.kits': 'kits',
    'wms.bench.scan_lpn': 'Bipe o pallet (LPN) de saída',
    'wms.bench.scan_lpn_help':
        'Use uma etiqueta de LPN em branco. Se o pallet desta OP já está na saída, bipe o mesmo para continuar nele.',
    'wms.bench.scan_other_lpn_help': 'Pallet cheio? Bipe outra etiqueta de LPN em branco: este vai para a logística.',
    'wms.bench.scan_lpn_first_title': 'Bipe o pallet primeiro',
    'wms.bench.scan_lpn_first': 'O primeiro bipe da OP é o pallet (LPN) onde os kits vão.',
    'wms.bench.lpn_refused': 'Pallet recusado',
    'wms.bench.scan_refused': 'Bipe recusado',
    'wms.bench.lpn_at_output': 'Pallet {lpn} na saída {location}: os kits vão para ele.',
    'wms.bench.lpn_forming':
        'Pallet {lpn} em formação: a saída {location} ainda tem o pallet anterior. Os kits já vão para ele; quando a logística retirar o anterior, bipe {lpn} de novo.',
    'wms.bench.lpn_state_at_output': 'Na saída {location}',
    'wms.bench.lpn_state_forming': 'Em formação (fora da saída)',
    'wms.bench.lpn_none': 'Nenhum pallet ainda: bipe uma etiqueta de LPN em branco.',
    'wms.bench.counter_done': 'Kits apontados',
    'wms.bench.counter_remaining': 'Faltam',
    'wms.bench.counter_target': 'meta: {planned} kits',
    'wms.bench.counter_progress': 'Progresso',
    'wms.production.inventory_id': 'Inventory ID do kit',
    'wms.production.planned_kits': 'Meta de kits (opcional)',
    'wms.production.planned_kits_help':
        'Ao chegar nesta quantidade de kits apontados, a bancada finaliza a OP sozinha. Vazio = sem meta: a OP é finalizada na bancada ou aqui.',
    'wms.production.bench_title': 'Bancada',
    'wms.production.bench_free': 'Livre — qualquer bancada pode abrir (depois de liberada)',
    'wms.production.bench_open_at': 'Aberta na máquina {machine}',
    'wms.production.bench_pause': 'Tirar da bancada',
    'wms.production.bench_paused': 'OP tirada da bancada — ficou livre',
    'wms.production.kits_progress': '{done} de {planned} kits apontados',
    'wms.production.kits_done': '{done} kits apontados',
    'wms.production.history': 'Histórico da OP',
    'wms.production.history_empty': 'Nada registrado ainda.',
    'wms.production_order_event.RELEASED': 'Liberada',
    'wms.production_order_event.OPENED': 'Aberta na bancada',
    'wms.production_order_event.PAUSED': 'Pausada',
    'wms.production_order_event.FINISHED': 'Finalizada',
    'wms.production_order_event.CANCELED': 'Cancelada',
    'wms.production_order_event.REMOVED_FROM_BENCH': 'Tirada da bancada',
    'wms.production_event_reason.pause': 'o operador pausou',
    'wms.production_event_reason.switch_order': 'o operador trocou de OP',
    'wms.production_event_reason.exit': 'o operador saiu da bancada',
    'wms.production_event_reason.operator_moved': 'o operador foi para outra máquina',
    'wms.production_event_reason.other_order_opened': 'outra OP foi aberta nesta máquina',
    'wms.production_event_reason.bench': 'na bancada',
    'wms.production_event_reason.console': 'pelo painel de produção',
    'wms.production_event_reason.target_reached': 'a meta de kits foi atingida',
    'wms.production.inventory_id_help':
        'Automático: a etiqueta do Full dos anúncios deste produto. É o que o operador bipa em cada kit pronto e o que a logística lê.',
    'wms.production.inventory_id_missing':
        'Este produto não tem inventory_id do Full (nenhum anúncio com etiqueta de Full): a bancada não teria o que conferir.',
    'wms.bench.pick_machine': 'Em qual máquina você está?',
    'wms.bench.no_machines': 'Nenhuma máquina ativa cadastrada.',
    'wms.bench.pick_order': 'Qual ordem de produção?',
    'wms.bench.no_orders': 'Nenhuma OP planejada ou liberada.',
    'wms.bench.change_machine': 'Trocar de máquina',
    'wms.bench.change_order': 'Trocar de OP',
    'wms.bench.printer_title': 'Impressora das etiquetas',
    'wms.bench.printer_help':
        'Vale para todo o MELIDASH neste computador: as etiquetas da bancada, de pallet e de endereço saem nesta impressora sem perguntar de novo.',
    'wms.bench.printer_current': 'Imprimindo em {printer}',
    'wms.bench.printer_none': 'Nenhuma impressora definida: as etiquetas não vão sair na impressora.',
    'wms.bench.printer_label': 'Impressora',
    'wms.bench.printer_change_hint': 'Para trocar, deslogue e escolha na tela inicial da bancada.',
    'wms.bench.your_bench': 'Sua bancada',
    'wms.bench.panel_title': 'Painel da máquina',
    'wms.bench.material_running': 'Material na boca da máquina',
    'wms.bench.material_none': 'Nenhum material capturado — capture a mãe para começar.',
    'wms.bench.material_since': 'Capturado em',
    'wms.bench.lot_current': 'Lote de produção vigente',
    'wms.bench.lot_none': 'Nenhum lote ainda — o LT nasce no primeiro bipe de lote.',
    'wms.bench.lot_carries': 'Este LT carrega',
    'wms.bench.lot_kit_open': 'Kit em aberto — a virada de lote está SEGURA até o kit fechar.',
    'wms.bench.capture_title': 'Capturar material',
    'wms.bench.capture_scan': 'Bipe o lote da etiqueta do FORNECEDOR (a que está colada na bobina)',
    'wms.bench.capture_hint': 'Capturar encerra o material anterior desta máquina e libera o novo.',
    'wms.bench.capture_done': 'Material capturado',
    'wms.bench.capture_previous_closed': 'Material anterior encerrado',
    'wms.bench.capture_lpn': 'Pallet de origem (opcional)',
    'wms.bench.capture_lpn_none': 'Sem pallet — só o lote',
    'wms.bench.consume_title': 'Consumir lote',
    'wms.bench.consume_scan': 'Bipe o lote do FORNECEDOR que está entrando na máquina',
    'wms.bench.consume_hint': 'A etiqueta é a do fornecedor, colada na bobina — não é o LPN do pallet.',
    'wms.bench.consume_pick_lpn': 'De qual pallet sai este lote?',
    'wms.bench.consume_qty': 'Quanto foi consumido?',
    'wms.bench.consume_available': 'Disponível no pallet',
    'wms.bench.consume_all': 'Consumir tudo',
    'wms.bench.consume_done': 'Consumo registrado',
    'wms.bench.lot_not_found_title': 'Lote não está na entrada',
    'wms.bench.lot_not_found_message':
        'Nenhum pallet das posições de entrada desta OP tem este lote. Confira a etiqueta ou peça o pallet ao Supervisor.',
    'wms.bench.lots_available': 'Lotes disponíveis na entrada',
    'wms.bench.no_input_stock': 'Nenhum pallet nas posições de entrada desta OP.',
    'wms.bench.finish_title': 'Fim de matéria-prima',
    'wms.bench.finish_confirm':
        'Confirmar que a matéria-prima acabou? O sistema encerra o material da máquina e MANDA O PEDIDO DE REABASTECIMENTO PARA O SUPERVISOR.',
    'wms.bench.finish_done': 'Pedido enviado ao Supervisor',
    'wms.bench.finish_service_order': 'Ordem de serviço criada (aguardando o Supervisor)',
    'wms.bench.finish_waiting':
        'A OS entrou na fila do Supervisor em BACKLOG. Ele aprova, troca o material ou nega — e a logística entrega na posição de entrada.',
    'wms.bench.produce_title': 'Apontar produção',
    'wms.bench.produce_kits': 'Quantos kits foram montados?',
    'wms.bench.produce_qty': 'Quantidade produzida',
    'wms.bench.produce_qty_derived': 'Quantidade (derivada dos kits)',
    'wms.bench.produce_loose': 'Apontar quantidade avulsa (sem kit)',
    'wms.bench.produce_with_kits': 'Voltar a apontar por kits',
    'wms.bench.produce_scrap': 'Quanto foi de refugo?',
    'wms.bench.produce_scrap_hint':
        'Refugo acima do teórico da ficha técnica gera um ALERTA de qualidade — o apontamento é registrado do mesmo jeito. Aponte o número real.',
    'wms.bench.produce_components': 'Componentes do kit',
    'wms.bench.produce_components_hint':
        'Bipe o pallet de cada componente que entrou no kit. O sistema confere se a combinação fecha a quantidade.',
    'wms.bench.produce_components_skip': 'Sem componentes (saída direta da máquina)',
    'wms.bench.produce_component_add': 'Bipe o pallet do componente',
    'wms.bench.produce_component_qty': 'Quantas peças deste componente?',
    'wms.bench.produce_component_registered': 'Combinações cadastradas para este kit',
    'wms.bench.produce_destination': 'Bipe a posição de SAÍDA (FAB-*-OT)',
    'wms.bench.produce_destination_hint': 'A logística coleta o produto acabado nesta posição.',
    'wms.bench.produce_review': 'Confere?',
    'wms.bench.produce_confirm': 'Apontar produção',
    'wms.bench.produce_done': 'Produção apontada',
    'wms.bench.produce_lpn_created': 'Pallet de produto acabado criado',
    'wms.bench.scrap_alert_title': 'Alerta de refugo',
    'wms.bench.scrap_alert_body':
        'O refugo apontado ({scrap}%) passou do teórico da ficha técnica ({theoretical}%). O apontamento FOI registrado — isto é dado de qualidade, não bloqueio.',
    'wms.bench.lot_turned_title': 'Virou o lote de produção',
    'wms.bench.lot_turned_body': 'Um LT novo abriu porque {reasons}. As próximas saídas saem com o lote novo.',
    'wms.bench.lot_kept_title': 'Mesmo lote de produção',
    'wms.bench.lot_held_title': 'Virada segura pelo kit em aberto',
    'wms.bench.lot_held_body':
        'O lote do fornecedor mudou, mas há um kit em montagem: ele vira KIT DE TRANSIÇÃO e fica ligado aos DOIS lotes. O LT novo abre no próximo kit.',
    'wms.bench.reason_FIRST_CONSUMPTION': 'é o primeiro consumo da OP',
    'wms.bench.reason_PRODUCTION_DAY_TURN': 'virou o dia de produção',
    'wms.bench.reason_SUPPLIER_LOT_CHANGE': 'mudou o lote do fornecedor',
    'wms.bench.previous_lot': 'Lote anterior',
    'wms.bench.new_lot': 'Lote novo',
    'wms.bench.back': 'Voltar',
    'wms.bench.exit': 'Sair da bancada',
    'wms.bench.refresh': 'Atualizar',
    'wms.bench.keypad_clear': 'Limpar',
    'wms.bench.keypad_backspace': 'Apagar',
    'wms.bench.confirm': 'Confirmar',
    'wms.bench.kit_label': 'Etiqueta do kit',
    'wms.bench.kit_label_hint': 'Produto + lote de produção + quantidade. Mesmo caminho de impressão do módulo (rede ou Impressora USB).',
    'wms.bench.another': 'Apontar outro',
    'wms.bench.order_not_operable': 'Esta OP não aceita mais apontamento.',

    // ── Ordens de Serviço & Rotas (M5) ───────────────────────────────────────
    // Vocabulário congelado ANTES da UI de propósito: console e coletor são
    // construídos em paralelo e precisam falar exatamente a mesma língua — "OS
    // pausada" tem que ler igual nos dois lados da operação.
    'wms.tab.service_orders': 'Ordens de Serviço',
    'wms.service_orders.title': 'Ordens de Serviço',
    'wms.service_orders.description':
        'A fila de trabalho do armazém. O Supervisor prepara e libera; o operador aceita no coletor e executa passo a passo. Toda OS carrega o SLA com as pausas descontadas.',
    'wms.service_orders.new': 'Nova OS',
    // ⚠️ A criação avulsa é a SAÍDA DE EMERGÊNCIA e a tela precisa dizer isso
    // antes do primeiro campo: aqui não há planejador, e quem espera a jornada
    // automática (cabeceira, pernas, troca de mão) vai estranhar o resultado.
    'wms.service_orders.new_help':
        'Saída de emergência: os itens são criados EXATAMENTE como você escrever, na ordem em que escrever. Aqui não há planejador — nenhuma perna, cabeceira ou troca de mão é montada. Para a jornada automática do pallet, use Alocar carga.',
    'wms.service_orders.new_items_help':
        'Cada linha é um item, e a ordem das linhas é a ordem em que o operador vai executar. O LPN é opcional: sem ele, o item é o que estiver na posição de origem.',
    'wms.service_orders.new_no_types': 'Nenhum tipo de OS ativo — cadastre em Configurações do Armazém.',
    'wms.service_orders.new_submit': 'Criar OS',
    'wms.service_orders.empty': 'Nenhuma OS nesta coluna.',

    // Kanban (§8.1.5) — as colunas E a raia de incidentes
    'wms.kanban.BACKLOG': 'Fila de decisão',
    'wms.kanban.BACKLOG_help':
        'Esperando a decisão do Supervisor. Reabastecimento pedido pela máquina nasce aqui: aprovar move para Liberadas; negar cancela.',
    'wms.kanban.READY': 'Liberadas',
    'wms.kanban.READY_help': 'Disponíveis no coletor. O primeiro operador que aceitar leva.',
    'wms.kanban.IN_PROGRESS': 'Em execução',
    'wms.kanban.DONE': 'Concluídas',
    'wms.kanban.incidents': 'Incidentes',
    'wms.kanban.incidents_help': 'Inclui o que está parado nas posições de incidente (INC-*).',
    'wms.kanban.approve': 'Aprovar',
    'wms.kanban.deny': 'Negar',

    // Status e campos da OS
    'wms.service_order.status.BACKLOG': 'Fila de decisão',
    'wms.service_order.status.READY': 'Liberada',
    'wms.service_order.status.IN_PROGRESS': 'Em execução',
    'wms.service_order.status.PAUSED': 'Pausada',
    'wms.service_order.status.DONE': 'Concluída',
    'wms.service_order.status.CANCELED': 'Cancelada',
    'wms.service_order.field.code': 'OS',
    'wms.service_order.field.type': 'Tipo',
    'wms.service_order.field.operator': 'Operador',
    'wms.service_order.field.equipment': 'Equipamento',
    'wms.service_order.field.items': 'Itens',
    'wms.service_order.field.route_algorithm': 'Rota escolhida',
    'wms.service_order.field.group': 'Par',
    'wms.service_order.field.sla': 'Tempo de execução',
    'wms.service_order.field.sla_help': 'Do aceite até a conclusão, descontando as pausas.',
    'wms.service_order.pair_notice': 'Esta OS tem par: as duas seguem a MESMA sequência de itens.',

    // Itens
    'wms.service_order_item.status.PENDING': 'Pendente',
    'wms.service_order_item.status.EXECUTING': 'Em execução',
    'wms.service_order_item.status.DONE': 'Concluído',
    'wms.service_order_item.status.SKIPPED_ALERT': 'Pulado com alerta',
    'wms.service_order_item.skipped_help': 'Item com problema não trava a rota: segue com incidente aberto.',
    'wms.service_order_item.from': 'De',
    'wms.service_order_item.to': 'Para',
    // Item de pallet inteiro sem destino: o operador escolhe no bipe; depois de
    // feito, o item mostra onde o pallet foi guardado.
    'wms.service_order_item.to_free': 'Escolhido pelo operador',

    // Pausa
    'wms.pause.title': 'Pausar OS',
    'wms.pause.reason': 'Motivo',
    'wms.pause.reason.LUNCH': 'Almoço',
    'wms.pause.reason.SHIFT_END': 'Fim de turno',
    'wms.pause.reason.INCIDENT': 'Incidente',
    'wms.pause.reason.OTHER': 'Outro',
    'wms.pause.drop_location': 'Onde a carga ficou',
    'wms.pause.drop_location_help':
        'Você está carregando um pallet: bipe o endereço onde ele ficou. Sem isso o sistema perde a carga de vista.',
    'wms.pause.resume': 'Retomar',
    'wms.pause.resume_same_operator': 'Só quem pausou pode retomar — identifique-se com o mesmo crachá.',

    // Aceite no coletor (first-wins)
    'wms.collector.service_orders_title': 'OSs liberadas',
    'wms.collector.accept': 'Aceitar OS',
    'wms.collector.accept_taken': 'Outro operador aceitou esta OS primeiro.',
    'wms.collector.already_active': 'Você já tem uma OS ativa. Pause-a antes de aceitar outra.',
    'wms.collector.waiting_pair': 'Aguardando o par',
    'wms.collector.waiting_pair_help':
        'A outra metade deste par ainda não entregou este item. Assim que ela concluir, o seu bipe é aceito.',
    'wms.collector.action_service_orders': 'Ordens de Serviço',
    'wms.collector.service_orders_hint': 'Aceite uma OS liberada e execute item a item.',
    'wms.collector.service_orders_empty': 'Nenhuma OS liberada agora. Fale com o Supervisor.',
    'wms.collector.service_orders_refresh': 'Atualizar lista',
    'wms.collector.my_service_order': 'Sua OS',
    'wms.collector.continue_service_order': 'Continuar',
    'wms.collector.open_active_service_order': 'Abrir a OS que está com você',
    'wms.collector.loading_order': 'Carregando a OS…',
    'wms.collector.item_counter': 'Item',
    'wms.collector.item_of': 'de',
    'wms.collector.go_to_origin': 'Vá até a origem e bipe a etiqueta',
    'wms.collector.go_to_destination': 'Leve até o destino e bipe a etiqueta',
    // Destino: a torre só aparece DEPOIS do bipe. Planejado → "Guardei em…";
    // livre (item sem destino) → o operador escolhe onde guardar.
    'wms.collector.planned_position_title': 'Guarde nesta posição',
    'wms.collector.stored_at': 'Guardei em',
    'wms.collector.free_destination_title': 'Você escolhe onde guardar',
    'wms.collector.free_destination_hint':
        'Leve o pallet até onde vai guardar e bipe a etiqueta do bloco ou do endereço.',
    'wms.collector.change_destination': 'Trocar destino',
    'wms.collector.destination_virtual_title': 'Não dá para guardar aqui',
    'wms.collector.destination_virtual_hint':
        'Esta é uma localização virtual. Bipe a etiqueta do bloco ou do endereço onde o pallet vai ficar.',
    'wms.collector.free_destination_short': 'Escolhido pelo operador',
    'wms.collector.take_lpn_hint': 'Pegue o pallet e bipe a etiqueta do LPN.',
    // ── Passo do pallet: o operador precisa saber, ANTES de bipar, se leva o
    // pallet todo ou só algumas unidades — é o que muda o que ele faz com as
    // mãos. E o passo das unidades, bipadas 1 a 1.
    'wms.collector.full_pallet_title': 'PALLET INTEIRO',
    'wms.collector.full_pallet_hint': 'Leve o pallet todo — não abra a carga.',
    'wms.collector.fractional_title': 'PALLET FRACIONADO',
    'wms.collector.fractional_lpn_hint': 'Bipe a etiqueta deste pallet para abrir a separação das unidades.',
    'wms.collector.pallet_stays': 'O pallet FICA na posição — só as unidades vão no carrinho.',
    'wms.collector.take_units_title': 'PEGUE',
    'wms.collector.unit_word': 'UNIDADE',
    'wms.collector.units_word': 'UNIDADES',
    'wms.collector.units_scan_hint': 'Bipe o código de barras de CADA unidade que tirar do pallet.',
    'wms.collector.units_counter': 'Separadas',
    'wms.collector.unit_reserved': 'Unidade separada — ponha no carrinho para o packing.',
    'wms.collector.unit_wait': 'Aguarde para bipar a próxima…',
    'wms.collector.units_complete': 'Unidades completas — leve o carrinho ao packing.',
    'wms.collector.unit_wrong_product': 'Unidade errada',
    'wms.collector.unit_wrong_product_hint':
        'Esta unidade não é a do item. Devolva ao pallet e bipe a unidade certa.',
    'wms.collector.unit_not_found': 'Código não encontrado',
    'wms.collector.unit_not_found_hint': 'Nenhum produto do catálogo tem este código de barras.',
    'wms.collector.lpn_free_choice': 'Este item não define o LPN — bipe o pallet que você vai levar.',
    'wms.collector.item_done_flash': 'Item concluído',
    'wms.collector.order_done_title': 'OS concluída',
    'wms.collector.order_canceled_title': 'OS cancelada',
    'wms.collector.order_canceled_hint': 'O Supervisor cancelou esta OS. Se você está com carga, procure-o.',
    'wms.collector.items_done': 'Concluídos',
    'wms.collector.items_skipped': 'Pulados',
    'wms.collector.back_to_orders': 'Voltar às OSs',
    'wms.collector.order_reload': 'Recarregar a OS',
    'wms.collector.pause_action': 'Pausar OS',
    'wms.collector.pause_confirm': 'Pausar agora',
    'wms.collector.paused_title': 'OS pausada',
    'wms.collector.carrying_title': 'Você está com um pallet',
    // "Pular item" nomeado pelo PROBLEMA, não pela ação: o operador não procura
    // "pular", ele procura o que está acontecendo na frente dele.
    'wms.collector.skip_action': 'Item com problema',
    'wms.collector.skip_title': 'Este item não deu para fazer?',
    'wms.collector.skip_explain':
        'O item fica marcado como pulado, um incidente é aberto com o seu crachá e a OS SEGUE para o próximo item. O Supervisor resolve depois.',
    'wms.collector.skip_kind': 'O que aconteceu',
    'wms.collector.skip_notes': 'Quer detalhar? (opcional)',
    'wms.collector.skip_notes_placeholder': 'Ex.: posição vazia, sem pallet nenhum.',
    'wms.collector.skip_confirm': 'Pular item e abrir incidente',
    'wms.collector.skip_done': 'Item pulado — o Supervisor foi avisado.',
    'wms.collector.pair_missing': 'O que falta',
    'wms.collector.pair_item': 'Item da OS irmã',
    'wms.collector.pair_retry': 'Já chegou — tentar de novo',
    'wms.collector.execute_failed_title': 'Não deu para gravar',

    // Alocação com preview de split (§8.1.4)
    'wms.allocation.title': 'Alocar carga',
    // Texto REESCRITO em 11/08: a versão anterior dizia "aérea gera o par,
    // térreo gera só transporte", que era o modelo antigo — o que decide a
    // divisão é a JORNADA, e juntar tudo numa OS só é escolha do Supervisor.
    'wms.allocation.description':
        'Escolha os LPNs e os destinos: o sistema mostra ANTES a jornada de cada pallet — da posição à cabeceira, o transporte entre ruas e a subida no destino — e quais OSs isso vira. Dá para juntar a jornada inteira numa OS só, e fatiar as levas como quiser antes de confirmar.',
    'wms.allocation.preview': 'Prévia das OSs',
    'wms.allocation.preview_pair': 'Par: vertical + transporte',
    'wms.allocation.preview_single': 'Só transporte (térreo)',
    'wms.allocation.confirm': 'Gerar OSs',
    'wms.allocation.split': 'Fatiar',

    // Comparador de rotas (§6.6)
    'wms.route.title': 'Comparador de rotas',
    // Texto REESCRITO em 11/08: "retorno ao staging a cada item" foi revogado
    // pela regra do encadeamento — cada trecho parte de onde o anterior parou.
    'wms.route.description':
        'A mesma lista de itens custeada por seis métodos, sobre o mapa do galpão. Cada trecho parte de onde o anterior terminou — e o primeiro, de onde o equipamento parou.',
    'wms.route.algorithm.EUCLIDEAN': 'Euclidiana',
    'wms.route.algorithm.MANHATTAN': 'Manhattan',
    'wms.route.algorithm.AISLE': 'Travessas',
    'wms.route.algorithm.ASTAR': 'Dijkstra / A*',
    'wms.route.algorithm.BRUTE_FORCE': 'Força bruta (exata)',
    'wms.route.algorithm.MANUAL': 'Manual (você monta)',
    'wms.route.method': 'Método',
    // "Menor custo" ≠ "Rota escolhida": um é o veredito do motor, o outro é a
    // decisão gravada na OS. Distinguir os dois na tela evita que o Supervisor
    // ache que escolher é obrigatório — ou que o sistema escolheu por ele.
    'wms.route.recommended': 'Menor custo',
    'wms.route.distance': 'Distância',
    'wms.route.time_total': 'Tempo estimado',
    'wms.route.time_travel': 'Deslocamento',
    'wms.route.time_vertical': 'Vertical',
    'wms.route.time_handling': 'Manuseio',
    'wms.route.choose': 'Usar esta rota',
    'wms.route.manual_hint': 'Toque nas posições no mapa, na ordem em que quer executá-las.',
    'wms.route.brute_force_capped': 'Itens demais para a busca exata — comparação feita até o limite seguro.',
    'wms.route.facing': 'Encara o lado',
    // ⚠️ Texto medido, não suposto: em muitas rotas os métodos EMPATAM mesmo
    // cruzando ruas (o ciclo volta à cabeceira a cada pallet, então a ordem
    // pesa pouco). Mas nem sempre — já foram medidos casos com 8 m de
    // diferença. A frase não pode prometer nem "dá tudo no mesmo" nem "sempre
    // muda": ela diz onde olhar quando os números se separarem.
    // ── A rota é desenhada SOBRE O MAPA (M5-3, 11/08) ───────────────────────
    // Palavras do dono: "o desenho da rota DEVE usar o sistema de mapa… mesmo
    // que as rotas não estejam dispostas lado a lado corretamente". O diagrama
    // paralelo morreu: era uma segunda descrição do galpão.
    'wms.route.map_title': 'Rota sobre o mapa do galpão',
    'wms.route.map_hint':
        'O percurso é o que o motor calculou sobre este mesmo desenho: as ruas tortas do mapa saem tortas na rota, e é assim que a distância foi cobrada.',
    'wms.route.map_unavailable': 'Sem o desenho do galpão não há como calcular nem desenhar a rota.',
    // Lado ENCARADO ao chegar — sai da GEOMETRIA do desenho (vetor corredor →
    // bloco), não de paridade: E/W/N/S na convenção do mapa (X cresce para a
    // direita, Y cresce para o fundo).
    'wms.route.side.E': 'direita (no mapa)',
    'wms.route.side.W': 'esquerda (no mapa)',
    'wms.route.side.N': 'fundo do galpão',
    'wms.route.side.S': 'frente (saída)',
    'wms.route.start_at': 'Parte de',
    'wms.route.end_at': 'Termina em',
    'wms.route.end_hint': 'A próxima rota parte daqui.',

    // ── A JORNADA do pallet (M5 r1, regras 1 a 4) ───────────────────────────
    'wms.allocation.journey': 'Jornada do pallet',
    'wms.allocation.leg': 'Perna',
    'wms.allocation.leg.1': 'Da posição até a cabeceira',
    'wms.allocation.leg.2': 'Transporte entre cabeceiras',
    'wms.allocation.leg.3': 'Da cabeceira até a posição',
    'wms.allocation.handoff': 'Troca de mão',
    'wms.allocation.handoff_hint':
        'Quem RECEBE espera quem ENTREGA, no endereço da troca. É a única espera que existe — e ela só aparece quando a jornada é dividida entre equipamentos.',
    'wms.allocation.merge': 'Juntar numa OS só',
    'wms.allocation.merge_hint':
        'Um equipamento faz a jornada inteira (tira da posição, leva e guarda): sem par e sem espera.',
    'wms.allocation.merge_on': 'Jornada inteira numa OS só',
    'wms.allocation.split_default': 'Dividida por comportamento (vertical numa OS, transporte noutra)',
    'wms.allocation.split.LIFT_ONLY': 'Só vertical',
    'wms.allocation.split.TRANSPORT_ONLY': 'Só transporte',
    'wms.allocation.split.MIXED': 'Vertical + transporte',
    'wms.allocation.direction.PUTAWAY': 'Guarda',
    'wms.allocation.direction.RETRIEVAL': 'Retirada',
    'wms.allocation.direction.TRANSFER': 'Remanejo (aéreo → aéreo)',
    'wms.allocation.direction.GROUND': 'Chão a chão',
    'wms.allocation.staging_codes': 'Cabeceiras usadas',

    // Espera pelo HANDOFF (regra 4) — o vínculo do par deixou de ser a `seq`
    'wms.service_order.handoff_from': 'Espera a entrega de',
    'wms.service_order.waiting_handoff': 'Aguardando a entrega do pallet',
    'wms.service_order.group_notice': 'Esta OS faz parte de uma jornada dividida entre equipamentos.',

    // Largura do bloco: grava no CADASTRO, não só no desenho (M4-4)
    'wms.map.block_width_note':
        'A largura do vão é gravada no CADASTRO do bloco (rack + bloco), além do desenho. O máximo de andares vale só para o desenho.',
    'wms.map.block_width_saved': 'Largura gravada no cadastro do bloco. Salve o mapa para guardar o desenho.',

    'wms.route.converge_hint':
        'É comum os métodos empatarem: o ciclo volta ao staging a cada pallet, então a ordem pesa pouco. Quando os números se separam, a diferença está na ordem das ruas — e aí a escolha vale metros.',

    // ── INBOUND contra ORDEM DE COMPRA (console de recebimento) ─────────────
    'wms.inbound.title': 'Recebimento (OC)',
    'wms.inbound.subtitle':
        'Escolha a ordem de compra, veja o esperado × recebido, registre o recebimento físico (o pallet nasce etiquetado) e confronte com a NF do fornecedor. Receber em parciais é o caso normal; divergência não trava — fica à vista.',
    'wms.inbound.list_title': 'Ordens de compra',
    'wms.inbound.scope.receivable': 'Abertas para recebimento',
    'wms.inbound.scope.all': 'Todas',
    'wms.inbound.search_placeholder': 'Buscar por código da OC ou fornecedor…',
    'wms.inbound.empty': 'Nenhuma OC encontrada neste filtro.',
    'wms.inbound.select_hint': 'Escolha uma OC na lista para abrir a ficha de recebimento.',
    'wms.inbound.col.order': 'OC',
    'wms.inbound.col.supplier': 'Fornecedor',
    'wms.inbound.col.progress': 'Recebido / pedido',
    'wms.inbound.col.status': 'Status',
    'wms.inbound.field.sku': 'SKU',
    'wms.inbound.field.product': 'Produto',
    'wms.inbound.field.ordered': 'Pedido',
    'wms.inbound.field.confirmed': 'Confirmado',
    'wms.inbound.field.received': 'Recebido',
    'wms.inbound.field.pending': 'Falta',
    'wms.inbound.field.delta': 'Desvio',
    'wms.inbound.items_title': 'Itens da OC (esperado × recebido)',
    'wms.inbound.receipts_title': 'Recebimentos registrados',
    'wms.inbound.receipts_empty': 'Nenhum recebimento físico registrado ainda.',
    'wms.inbound.lpns_title': 'Pallets desta OC',
    'wms.inbound.receive_action': 'Registrar recebimento',
    'wms.inbound.receive_title': 'Registrar recebimento físico',
    'wms.inbound.receive_hint':
        'Uma confirmação = um pallet (LPN novo etiquetado). Chegou em mais de um pallet? Registre um de cada vez. As quantidades pré-preenchidas são o que FALTA de cada item — ajuste para o que realmente chegou.',
    'wms.inbound.receive_confirm': 'Confirmar recebimento',
    'wms.inbound.receive_success': 'Recebimento registrado — pallet criado.',
    'wms.inbound.not_receivable':
        'Esta OC não aceita recebimento neste status. Recebe-se de OPEN (aguardando fornecedor) a SHIPPED (em trânsito).',
    'wms.inbound.status.PENDING': 'Nada recebido',
    'wms.inbound.status.PARTIAL': 'Parcial',
    'wms.inbound.status.OK': 'Completo',
    'wms.inbound.status.OVER': 'Sobra',
    'wms.inbound.status.NOT_ON_ORDER': 'Fora do pedido',
    'wms.inbound.extra_title': 'Recebido FORA do pedido',
    'wms.inbound.warning_title': 'Divergências registradas (não travam)',
    'wms.inbound.totals.ordered_value': 'Valor pedido',
    'wms.inbound.totals.received_value': 'Valor recebido (custo da OC)',
    'wms.inbound.totals.invoices_total': 'Total das NFs',
    'wms.inbound.totals.invoice_delta': 'NF − recebido',

    // NF do fornecedor (confronto)
    'wms.inbound.invoices_title': 'Notas fiscais do fornecedor',
    'wms.inbound.invoices_empty':
        'Nenhuma NF registrada. Os acervos locais de NF-e não contêm notas de COMPRA (são as notas emitidas pela empresa) — registre a chave da NF do fornecedor aqui para o confronto.',
    'wms.inbound.invoice_add': 'Registrar NF',
    'wms.inbound.invoice_key': 'Chave de acesso (44 dígitos)',
    'wms.inbound.invoice_lookup': 'Consultar chave',
    'wms.inbound.invoice_lookup_found': 'Nota ENCONTRADA no acervo local — dados preenchidos.',
    'wms.inbound.invoice_lookup_not_found':
        'Nota não encontrada nos acervos locais (o esperado para NF de compra) — informe os dados do DANFE.',
    'wms.inbound.invoice_lookup_unconfigured': 'Acervo local de NF-e não configurado nesta instalação.',
    'wms.inbound.invoice_registered_elsewhere': 'Esta chave já está registrada.',
    'wms.inbound.invoice_number': 'Número',
    'wms.inbound.invoice_series': 'Série',
    'wms.inbound.invoice_issued_at': 'Emissão',
    'wms.inbound.invoice_total': 'Valor total (R$)',
    'wms.inbound.invoice_issuer': 'Emitente',
    'wms.inbound.invoice_issuer_cnpj': 'CNPJ do emitente (da chave)',
    'wms.inbound.invoice_save': 'Registrar NF',
    'wms.inbound.invoice_saved': 'NF registrada e vinculada à OC.',

    // ── ENTRADA DE DEVOLUÇÕES (WMS) ─────────────────────────────────────────
    'wms.returns.title': 'Devoluções (entrada)',
    'wms.returns.subtitle':
        'Registre a chegada do que voltou de cliente e separe item a item: volta ao estoque vendável (posição de devolução-boa) ou retorno ao fornecedor. O saldo nasce na separação, com o pallet etiquetado.',
    'wms.returns.new_action': 'Registrar chegada',
    'wms.returns.new_title': 'Registrar chegada de devolução',
    'wms.returns.new_hint': 'Só o cabeçalho: a caixa ainda não foi aberta. A separação por item vem depois, na ficha.',
    'wms.returns.field.reference': 'Referência (venda/pack/rastreio)',
    'wms.returns.field.notes': 'Observações',
    'wms.returns.col.code': 'Entrada',
    'wms.returns.col.reference': 'Referência',
    'wms.returns.col.items': 'Itens triados',
    'wms.returns.col.status': 'Status',
    'wms.returns.status.OPEN': 'Aberta',
    'wms.returns.status.DONE': 'Concluída',
    'wms.returns.empty': 'Nenhuma entrada de devolução registrada.',
    'wms.returns.select_hint': 'Escolha uma entrada na lista para abrir a separação.',
    'wms.returns.items_title': 'Itens separados',
    'wms.returns.items_empty': 'Nada separado ainda — a mercadoria está na mesa de triagem, fora do estoque.',
    'wms.returns.triage_action': 'Separar itens',
    'wms.returns.triage_title': 'Separar itens da devolução',
    'wms.returns.triage_hint':
        'Uma confirmação = um pallet (LPN novo) com os itens do MESMO destino. O depósito vem da regra de depósito do evento; a posição de devolução-boa é a do setor DEVOLUCAO.',
    'wms.returns.disposition': 'Destino',
    'wms.returns.disposition.SELLABLE': 'Volta ao estoque vendável',
    'wms.returns.disposition.SUPPLIER_RMA': 'Vai ao fornecedor (RMA)',
    'wms.returns.triage_confirm': 'Confirmar separação',
    'wms.returns.triage_success': 'Separação registrada — pallet criado.',
    'wms.returns.summary.sellable': 'Volta ao vendável',
    'wms.returns.summary.supplier_rma': 'Vai ao fornecedor',
    'wms.returns.mark_done': 'Concluir separação',
    'wms.returns.reopen': 'Reabrir',
    'wms.returns.done_hint': 'Entrada concluída — reabra para separar mais itens.',
    'wms.returns.convention_note':
        'Convenção da devolução-boa: pallet RETURN em depósito vendável, estacionado em posição do setor DEVOLUCAO — é essa posição que o picking prioriza. Fora do setor não trava: fica um aviso.',
};

/** Prefixo do rótulo de um parâmetro: `wms.setting.<chave>` (mesmo do backend). */
export const SETTING_LABEL_PREFIX = 'wms.setting.';

// Cache de módulo: o dicionário remoto é buscado UMA vez por sessão e
// compartilhado entre as páginas/abas do módulo (dado de apresentação, muda raro).
let cachedRemoteLabels = null;
let pendingFetch = null;

function normalizeRemoteLabels(payload) {
    const source = payload && typeof payload === 'object' && payload.labels ? payload.labels : payload;
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
        return null;
    }
    const dict = {};
    for (const [key, value] of Object.entries(source)) {
        if (typeof value === 'string' && value.trim()) {
            dict[key] = value;
        }
    }
    return Object.keys(dict).length > 0 ? dict : null;
}

function fetchRemoteLabels() {
    if (cachedRemoteLabels) {
        return Promise.resolve(cachedRemoteLabels);
    }
    if (!pendingFetch) {
        pendingFetch = api
            .getWarehouseLabels()
            .then((payload) => {
                cachedRemoteLabels = normalizeRemoteLabels(payload);
                return cachedRemoteLabels;
            })
            .catch(() => null)
            .finally(() => {
                pendingFetch = null;
            });
    }
    return pendingFetch;
}

/**
 * Hook de rótulos do módulo Armazém. Retorna `label(key)`: valor do backend
 * quando disponível, senão o fallback local, senão a própria chave (nunca
 * quebra a UI). `label` é estável enquanto o dicionário remoto não muda —
 * seguro como dependência de useMemo/useEffect.
 */
export function useWarehouseLabels() {
    const [remoteLabels, setRemoteLabels] = useState(cachedRemoteLabels);

    useEffect(() => {
        if (cachedRemoteLabels) {
            return undefined;
        }
        let alive = true;
        fetchRemoteLabels().then((dict) => {
            if (alive && dict) {
                setRemoteLabels(dict);
            }
        });
        return () => {
            alive = false;
        };
    }, []);

    // `params` preenche os `{nome}` do texto ("{count} etiqueta(s)"): sem isto a
    // tela mostrava o placeholder cru — o dicionário já usava a sintaxe, a
    // função não a honrava.
    const label = useCallback(
        (key, params = null) => {
            const text = remoteLabels?.[key] ?? WAREHOUSE_LABEL_FALLBACK[key] ?? key;
            if (!params || typeof text !== 'string') {
                return text;
            }
            return text.replace(/\{(\w+)\}/g, (match, name) =>
                params[name] === undefined || params[name] === null ? match : String(params[name]),
            );
        },
        [remoteLabels],
    );

    return { label, remoteLoaded: Boolean(remoteLabels) };
}

/**
 * "1 produto" / "3 produtos" — o que o pallet carrega DENTRO.
 *
 * Quatro telas mostram esse número ao lado do código do LPN e as quatro
 * concatenavam um "itens" invariável: escreviam "1 itens", e num lugar que só
 * comporta UM pallet isso se lia como contagem de LPN, não do conteúdo dele.
 * Fica aqui, junto do dicionário, para as quatro não divergirem de novo.
 */
export function FormatLpnProductCount(count, label) {
    const total = Number(count ?? 0);
    const suffix = label(
        total === 1 ? 'wms.panel.lpn_products_suffix_one' : 'wms.panel.lpn_products_suffix',
    );
    return `${FormatInt(total)} ${suffix}`;
}
